import React, { useState, useMemo, useEffect, useRef, Suspense, lazy } from 'react';
import {
  LayoutDashboard,
  FileText,
  FileInput,
  ChevronDown,
  Repeat,
  Users,
  SettingsIcon,
  Clock,
  BarChart3,
  Menu,
  X,
  Plus,
  Edit3,
  Printer,
  Send,
  CheckCircle,
  AlertCircle,
  Copy,
  Trash2,
  Power as LogOut,
  Mail,
} from './components/Icons.jsx';
import { SettingsModal } from './components/SettingsModal.jsx';
import { PrintView } from './components/PrintView.jsx';
import { InvoicePdfContent } from './components/InvoicePdfContent.jsx';
import { QuotePdfContent } from './components/QuotePdfContent.jsx';
import { Toast, ConfirmModal } from './components/UI.jsx';
import {
  formatCurrency,
  formatDate,
  addDays,
  addInterval,
  getIntervalLabel,
  getCustomerFullName,
} from './utils/format.js';
import { calculateDocumentTotals } from './utils/totals.js';
import {
  initialCompanySettings,
  initialCustomers,
  initialInvoices,
  initialQuotes,
  initialSubscriptions,
} from './data/initialState.js';
import { hasApi, API_BASE_URL } from './api/config.js';
import * as api from './api/ionos.js';
import AdminPanel from './components/AdminPanel.jsx';

const Dashboard = lazy(() => import('./views/Dashboard.jsx'));
const Invoices = lazy(() => import('./views/Invoices.jsx'));
const Quotes = lazy(() => import('./views/Quotes.jsx'));
const Subscriptions = lazy(() => import('./views/Subscriptions.jsx'));
const Customers = lazy(() => import('./views/Customers.jsx'));
const Tasks = lazy(() => import('./views/Tasks.jsx'));
const Reports = lazy(() => import('./views/Reports.jsx'));
const SentEmails = lazy(() => import('./views/SentEmails.jsx'));

const COMPANY_ID_KEY = 'devsuite_company_id';
const COMPANY_DISPLAY_KEY = 'company_display';

export default function App() {
  const isPopNavigationRef = useRef(false);
  const hasBootstrappedHistoryRef = useRef(false);

  const [activeTab, setActiveTabState] = useState('dashboard');
  const [isInvoicesMenuOpen, setIsInvoicesMenuOpen] = useState(false);
  const [isOrdersMenuOpen, setIsOrdersMenuOpen] = useState(false);

  // Custom Tab Handler to reset views
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    // Reset all editing states
    setIsEditingInvoice(false);
    setCurrentInvoice(null);
    setNextInvoiceId(null);

    setIsEditingQuote(false);
    setCurrentQuote(null);

    setIsEditingSubscription(false);
    setCurrentSubscription(null);

    setIsEditingCustomer(false);
    setCurrentCustomer(null);
    setViewCustomerId(null); // Also reset customer detail view

    // Optionally reset filters if desired, but user asked for "Overview", which usually implies list view.
    // Keeping filters as is for now, but ensure we are in list mode (which is handled by isEditing... = false).
  };
  const isInvoicesTabActive = ['invoices', 'subscriptions'].includes(activeTab);

  const handleInvoicesMenuClick = () => {
    setInvoiceFilter('all');
    setActiveTab('invoices');
    setIsInvoicesMenuOpen(true);
  };

  const handleInvoicesSubTabClick = (tabId) => {
    setIsInvoicesMenuOpen(true);
    if (tabId === 'recurring') {
      setActiveTab('subscriptions');
      return;
    }
    setActiveTab('invoices');
    if (tabId === 'reminders') setInvoiceFilter('reminders');
    if (tabId === 'credits') setInvoiceFilter('credits');
  };

  const isOrdersTabActive = ['quotes', 'order-confirmations', 'delivery-notes'].includes(activeTab);

  const handleOrdersSubTabClick = (tabId) => {
    setActiveTab(tabId);
    if (tabId === 'order-confirmations') {
      showToast('Auftragsbestätigungen werden als eigener Bereich vorbereitet.', 'info');
    }
    if (tabId === 'delivery-notes') {
      showToast('Lieferscheine werden als eigener Bereich vorbereitet.', 'info');
    }
  };
  const handleOrdersMenuClick = () => {
    setActiveTab('quotes');
    setIsOrdersMenuOpen(true);
  };
  const [loginState, setLoginState] = useState({ email: '', password: '', loading: false, error: '' });
  const [twoFactorState, setTwoFactorState] = useState({
    challengeToken: '',
    emailMasked: '',
    code: '',
    trustDevice: true,
    expiresAt: 0,
    loading: false,
    resendLoading: false,
    error: '',
  });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotState, setForgotState] = useState({ email: '', loading: false, message: '', error: '' });
  const [resetState, setResetState] = useState({ password: '', confirmPassword: '', loading: false, message: '', error: '' });
  const resetToken = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('reset_token') || '';
  }, []);
  const [companyId, setCompanyId] = useState(() => {
    if (typeof localStorage !== 'undefined') return localStorage.getItem(COMPANY_ID_KEY);
    return null;
  });
  const [user, setUser] = useState(null);

  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem('devsuite_user') || 'null');
      if (u) setUser(u);
    } catch { }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (hasBootstrappedHistoryRef.current) return;
    const current = window.history.state;
    if (!current || current.__nextbill !== true) {
      window.history.replaceState({ __nextbill: true, tab: activeTab }, '', window.location.href);
      window.history.pushState({ __nextbill: true, tab: activeTab }, '', window.location.href);
    }
    hasBootstrappedHistoryRef.current = true;
  }, [activeTab]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!hasBootstrappedHistoryRef.current) return;
    if (isPopNavigationRef.current) {
      isPopNavigationRef.current = false;
      return;
    }
    const current = window.history.state;
    if (!current || current.__nextbill !== true || current.tab !== activeTab) {
      window.history.pushState({ __nextbill: true, tab: activeTab }, '', window.location.href);
    }
  }, [activeTab]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPopState = (e) => {
      const state = e.state;
      if (state && state.__nextbill === true && typeof state.tab === 'string') {
        if (state.tab !== activeTab) {
          isPopNavigationRef.current = true;
          setActiveTab(state.tab);
        }
        return;
      }
      window.history.pushState({ __nextbill: true, tab: activeTab }, '', window.location.href);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [activeTab]);



  const [companySettings, setCompanySettings] = useState(() => {
    try {
      const saved = typeof localStorage !== 'undefined' && localStorage.getItem(COMPANY_DISPLAY_KEY);
      if (saved) {
        const o = JSON.parse(saved);
        if (!o.logoUrl && initialCompanySettings.logoUrl) {
          o.logoUrl = initialCompanySettings.logoUrl;
        }
        return { ...initialCompanySettings, ...o };
      }
    } catch (_) { }
    return initialCompanySettings;
  });

  const [customers, setCustomers] = useState(initialCustomers);
  const [invoices, setInvoices] = useState(initialInvoices);
  const [quotes, setQuotes] = useState(initialQuotes);
  const [subscriptions, setSubscriptions] = useState(initialSubscriptions);
  const [tasks, setTasks] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);

  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [pdfGenData, setPdfGenData] = useState(null);
  const pdfContainerRef = useRef(null);

  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const showToast = (message, type = 'info') => setToast(message ? { message, type } : null);
  const showConfirm = (message, onConfirm, onCancel, options = {}) => setConfirmDialog({ message, onConfirm, onCancel: onCancel || (() => { }), ...options });
  const showEmailSentConfirmation = ({ to, subject, docType, docId }) => {
    const label = docType === 'invoice' ? 'Rechnung' : 'Angebot';
    // Zeitversetzt öffnen, damit ein ggf. gerade geschlossener Confirm-Dialog
    // die neue Bestätigung nicht sofort wieder überschreibt.
    setTimeout(() => {
      showConfirm(
        `E-Mail erfolgreich versendet.\n\nTyp: ${label}\nNummer: ${docId || '-'}\nEmpfänger: ${to || '-'}\nBetreff: ${subject || '-'}`,
        () => { },
        () => { },
        { confirmLabel: 'OK', cancelLabel: 'Schließen' }
      );
    }, 50);
  };

  const [emailSending, setEmailSending] = useState(false);
  const [emailProgress, setEmailProgress] = useState(0);
  const emailProgressRef = useRef(0);
  const emailTimerRef = useRef(null);

  const startEmailProgress = () => {
    setEmailSending(true);
    emailProgressRef.current = 0;
    setEmailProgress(0);
    if (emailTimerRef.current) clearInterval(emailTimerRef.current);
    emailTimerRef.current = setInterval(() => {
      emailProgressRef.current = Math.min(95, emailProgressRef.current + 5);
      setEmailProgress(emailProgressRef.current);
    }, 300);
  };

  const finishEmailProgress = () => {
    if (emailTimerRef.current) {
      clearInterval(emailTimerRef.current);
      emailTimerRef.current = null;
    }
    setEmailProgress(100);
    setTimeout(() => {
      setEmailSending(false);
      emailProgressRef.current = 0;
      setEmailProgress(0);
    }, 500);
  };

  const safePdfFilename = (name) => {
    if (!name || typeof name !== 'string') return 'Kunde';
    const n = String(name)
      .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
      .replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue');
    return n.replace(/[^a-zA-Z0-9\s\-_]/g, '').replace(/\s+/g, '_').trim().slice(0, 60) || 'Kunde';
  };

  const buildServerPdfUrl = (type, id) => {
    if (!hasApi() || !id) return null;
    const endpoint = type === 'quote' ? 'download-quote-pdf.php' : 'download-invoice-pdf.php';
    return `${API_BASE_URL}/${endpoint}?id=${encodeURIComponent(id)}&v=${Date.now()}`;
  };
  const buildServerHtmlPreviewUrl = (type, id) => {
    if (!hasApi() || !id) return null;
    const docType = type === 'quote' ? 'quote' : 'invoice';
    return `${API_BASE_URL}/preview-document-html.php?type=${encodeURIComponent(docType)}&id=${encodeURIComponent(id)}&v=${Date.now()}`;
  };

  const openPdfPreview = ({ type, doc, customer }) => {
    if (!doc?.id) return;
    const resolvedCustomer = customer || customers.find((c) => c.id === doc?.customerId) || {};
    const pdfUrl = buildServerPdfUrl(type, doc.id);
    const htmlUrl = buildServerHtmlPreviewUrl(type, doc.id);
    setPreviewDoc({ type, doc, customer: resolvedCustomer, pdfUrl, htmlUrl });
  };

  const handleDownloadPdf = ({ type, doc, customer, settings }) => {
    if (!doc?.id) {
      showToast('Dokument ohne Nummer kann nicht als PDF heruntergeladen werden.', 'error');
      return;
    }
    const resolvedCustomer = customer || customers.find((c) => c.id === doc?.customerId) || {};
    const issues = [];
    if (!resolvedCustomer?.name) issues.push('Kundenname fehlt');
    if (!resolvedCustomer?.address) issues.push('Kundenadresse fehlt');
    if (!settings?.companyName) issues.push('Firmenname fehlt');
    if (!settings?.email) issues.push('Firmen-E-Mail fehlt');
    if ((type === 'invoice' && !doc?.dueDate) || (type === 'quote' && !doc?.expiryDate)) {
      issues.push(type === 'invoice' ? 'Fälligkeitsdatum fehlt' : 'Gültig-bis-Datum fehlt');
    }
    if (!Array.isArray(doc?.items) || doc.items.length === 0) issues.push('Keine Positionen im Dokument');
    const downloadDirect = () => {
      if (hasApi() && doc?.id) {
        const pdfUrl = buildServerPdfUrl(type, doc.id);
        if (pdfUrl) {
          window.open(pdfUrl, '_blank', 'noopener,noreferrer');
          showToast('PDF wird geöffnet.', 'success');
          return;
        }
      }
      openPdfPreview({ type, doc, customer: resolvedCustomer });
    };
    if (issues.length > 0) {
      showConfirm(
        `PDF-Checkliste:\n- ${issues.join('\n- ')}\n\nTrotzdem als PDF speichern?`,
        downloadDirect,
        undefined,
        { confirmLabel: 'Trotzdem speichern', cancelLabel: 'Abbrechen' }
      );
      return;
    }
    downloadDirect();
  };

  useEffect(() => {
    if (!hasApi() || !companyId) return;
    let cancelled = false;
    setApiLoading(true);
    setApiError(null);
    const timeout = setTimeout(() => {
      if (!cancelled) {
        setApiLoading(false);
        setApiError('Zeitüberschreitung beim Laden. Daten ggf. veraltet.');
      }
    }, 10000);
    (async () => {
      try {
        const results = await Promise.allSettled([
          api.apiGetSettings(),
          api.apiGetCustomers(),
          api.apiGetInvoices(),
          api.apiGetQuotes(),
          api.apiGetSubscriptions(),
        ]);
        if (cancelled) return;
        const [settingsRes, customersRes, invoicesRes, quotesRes, subsRes] = results;

        if (settingsRes.status === 'fulfilled' && settingsRes.value && typeof settingsRes.value === 'object') {
          const settings = settingsRes.value;
          setCompanySettings((prev) => {
            const next = { ...prev, ...settings };
            try {
              localStorage.setItem(COMPANY_DISPLAY_KEY, JSON.stringify({ companyName: next.companyName, logoUrl: next.logoUrl }));
            } catch (_) { }
            return next;
          });
        }
        if (customersRes.status === 'fulfilled' && Array.isArray(customersRes.value)) setCustomers(customersRes.value);
        if (invoicesRes.status === 'fulfilled' && Array.isArray(invoicesRes.value)) {
          setInvoices(invoicesRes.value);
        } else if (invoicesRes.status === 'rejected' && !cancelled) {
          showToast('Rechnungen: ' + (invoicesRes.reason?.message || invoicesRes.reason), 'error');
        } else if (invoicesRes.status === 'fulfilled' && !Array.isArray(invoicesRes.value) && !cancelled) {
          const msg = invoicesRes.value?.message || invoicesRes.value?.error || JSON.stringify(invoicesRes.value).slice(0, 120);
          showToast('Rechnungen (ungültige Antwort): ' + msg, 'error');
        }
        if (quotesRes.status === 'fulfilled' && Array.isArray(quotesRes.value)) {
          setQuotes(quotesRes.value);
        } else if (quotesRes.status === 'rejected' && !cancelled) {
          showToast('Angebote: ' + (quotesRes.reason?.message || quotesRes.reason), 'error');
        } else if (quotesRes.status === 'fulfilled' && !Array.isArray(quotesRes.value) && !cancelled) {
          const msg = quotesRes.value?.message || quotesRes.value?.error || JSON.stringify(quotesRes.value).slice(0, 120);
          showToast('Angebote (ungültige Antwort): ' + msg, 'error');
        }
        if (subsRes.status === 'fulfilled' && Array.isArray(subsRes.value)) setSubscriptions(subsRes.value);
        if (hasApi()) {
          api.apiGetTasks().then((tasksData) => { if (Array.isArray(tasksData)) setTasks(tasksData); }).catch(() => { });
          api.apiGetSentEmails().then((data) => { if (Array.isArray(data)) setSentEmails(data); }).catch(() => { });
        }
      } catch (err) {
        if (!cancelled) setApiError(err.message || 'Fehler beim Laden der Daten');
      } finally {
        if (!cancelled) {
          setApiLoading(false);
          clearTimeout(timeout);
        }
      }
    })();
    return () => { cancelled = true; clearTimeout(timeout); };
  }, []);

  useEffect(() => {
    if (!hasApi() || !companyId) return;
    const today = new Date().toISOString().slice(0, 10);
    const runKey = `nextbill_automation_last_run_${companyId}`;
    const disabledKey = `nextbill_automation_disabled_${companyId}`;
    if (localStorage.getItem(disabledKey) === '1') return;
    const lastRun = localStorage.getItem(runKey);
    if (lastRun === today) return;

    (async () => {
      try {
        const res = await api.apiRunAutomation();
        localStorage.setItem(runKey, today);
        const created = Number(res?.subscriptions?.created || 0);
        const reminders = Number(res?.reminders?.updated || 0);
        if (created > 0 || reminders > 0) {
          const [invoicesData, subsData] = await Promise.all([
            api.apiGetInvoices(),
            api.apiGetSubscriptions(),
          ]);
          if (Array.isArray(invoicesData)) setInvoices(invoicesData);
          if (Array.isArray(subsData)) setSubscriptions(subsData);
          showToast(`Automatik ausgeführt: ${created} Abo-Rechnungen, ${reminders} Mahnstufen.`, 'success');
        }
      } catch (err) {
        // Endpoint optional: wenn nicht vorhanden, Auto-Run dauerhaft deaktivieren.
        const msg = String(err?.message || '');
        if (msg.includes('404')) {
          localStorage.setItem(disabledKey, '1');
        }
        // Silent fallback: regulärer App-Betrieb soll nicht blockiert werden.
      }
    })();
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    const today = new Date();
    const todayIso = today.toISOString().slice(0, 10);
    const contractAlertDays = Math.max(1, Number(companySettings.contractAlertDays || 30));
    const contractLimit = new Date(today);
    contractLimit.setDate(contractLimit.getDate() + contractAlertDays);

    const createAutoTask = async (taskKey, payload) => {
      if (localStorage.getItem(taskKey) === todayIso) return;
      try {
        if (hasApi()) {
          const t = await api.apiCreateTask(payload);
          setTasks((prev) => [t, ...prev]);
        } else {
          setTasks((prev) => [{ id: Date.now() + Math.random(), ...payload, completed: 0 }, ...prev]);
        }
        localStorage.setItem(taskKey, todayIso);
      } catch {
        // ignore automation task errors
      }
    };

    invoices.forEach((inv) => {
      if (inv.status === 'paid' || inv.status === 'cancelled' || inv.status === 'credited') return;
      if (!inv.dueDate) return;
      if (new Date(inv.dueDate) >= today) return;
      const taskKey = `nb_sla_overdue_${companyId}_${inv.id}`;
      const customerName = getCustomerNameById(inv.customerId);
      createAutoTask(taskKey, {
        title: `SLA: Überfällige Rechnung ${inv.id}`,
        description: `Kunde ${customerName}: Rechnung seit ${formatDate(inv.dueDate)} überfällig.`,
        dueDate: todayIso,
        customerId: inv.customerId || null,
      });
    });

    subscriptions.forEach((sub) => {
      if (!sub.active || !sub.contractEndDate) return;
      const end = new Date(sub.contractEndDate);
      if (end < today || end > contractLimit) return;
      const taskKey = `nb_sla_contract_${companyId}_${sub.id}`;
      const customerName = getCustomerNameById(sub.customerId);
      createAutoTask(taskKey, {
        title: `SLA: Vertrag endet bald (${sub.id})`,
        description: `Kunde ${customerName}: Vertrag endet am ${formatDate(sub.contractEndDate)}.`,
        dueDate: sub.contractEndDate,
        customerId: sub.customerId || null,
      });
    });
  }, [companyId, invoices, subscriptions, companySettings.contractAlertDays]);

  useEffect(() => {
    if (!pdfGenData || !pdfContainerRef.current) return;
    const { type, doc, customer } = pdfGenData;
    const action = pdfGenData.action || 'upload';
    if (action === 'upload' && !hasApi()) {
      setPdfGenData(null);
      showToast('PDF-Upload ist nur mit API-Verbindung verfügbar.', 'error');
      return;
    }
    const id = doc.id;
    const customerName = safePdfFilename(customer?.name);
    const pdfFilename = type === 'quote' ? `Angebot_${customerName}_${id}.pdf` : `Rechnung_${customerName}_${id}.pdf`;
    const el = pdfContainerRef.current;
    const transparentPixel = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';

    const timer = setTimeout(() => {
      const waitForImages = (container) => {
        const imgs = container.querySelectorAll('img');
        if (imgs.length === 0) return Promise.resolve();
        return Promise.all(
          Array.from(imgs).map(
            (img) =>
              new Promise((resolve) => {
                if (img.complete && img.naturalWidth > 0) return resolve();
                img.onload = resolve;
                img.onerror = resolve;
                setTimeout(resolve, 2000);
              })
          )
        );
      };
      waitForImages(el).then(() => {
        void el.offsetHeight;
        const doCapture = () => {
          const target = el.firstElementChild || el;
          requestAnimationFrame(() => {
            import('html2pdf.js').then(({ default: html2pdf }) => {
              html2pdf()
                .set({
                  margin: 0,
                  filename: pdfFilename,
                  image: { type: 'png', quality: 1 },
                  html2canvas: {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    onclone: (clonedDoc) => {
                      const imgs = clonedDoc.querySelectorAll('img');
                      imgs.forEach((img) => {
                        const src = img.getAttribute('src') || '';
                        if (!src || src.startsWith('data:') || src.startsWith('blob:') || src.startsWith('/')) return;
                        try {
                          const url = new URL(src, window.location.href);
                          if (url.origin !== window.location.origin) {
                            img.setAttribute('src', transparentPixel);
                            img.setAttribute('alt', '');
                          }
                        } catch (_) {
                          img.setAttribute('src', transparentPixel);
                        }
                      });
                    },
                  },
                  jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                  pagebreak: { mode: ['css', 'legacy'] },
                })
                .from(target)
                .outputPdf('blob')
                .then((blob) => {
                  if (!blob || blob.size < 2000) {
                    throw new Error('Das erzeugte PDF ist zu klein oder leer. Bitte erneut versuchen.');
                  }
                  if (action === 'download') {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = pdfFilename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                    showToast('PDF wurde heruntergeladen.', 'success');
                    return null;
                  }
                  return type === 'quote' ? api.apiUploadQuotePdf(id, blob) : api.apiUploadInvoicePdf(id, blob);
                })
                .then((data) => {
                  if (action === 'download') return;
                  const pending = sendAfterPdfRef.current;
                  if (type === 'quote') {
                    setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, pdfPath: data.path } : q)));
                    if (pending && pending.type === 'quote' && pending.quoteId === id) {
                      sendAfterPdfRef.current = null;
                      api.apiSendQuoteEmail({
                        quoteId: id,
                        to: pending.customer.email,
                        subject: pending.subject,
                        body: pending.body,
                      })
                        .then(() => {
                          setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, status: 'sent' } : q)));
                          if (hasApi()) api.apiUpdateQuote({ ...pdfGenData.doc, status: 'sent' }).catch(() => { });
                          api.apiGetSentEmails().then((data) => { if (Array.isArray(data)) setSentEmails(data); }).catch(() => { });
                          showToast('E-Mail mit PDF-Anhang wurde versendet. Status auf "Gesendet" gesetzt.', 'success');
                          showEmailSentConfirmation({ to: pending.customer.email, subject: pending.subject, docType: 'quote', docId: id });
                        })
                        .catch((err) => {
                          showToast('E-Mail-Versand fehlgeschlagen: ' + (err?.message || err), 'error');
                        });
                    }
                  } else {
                    setInvoices((prev) => prev.map((inv) => (inv.id === id ? { ...inv, pdfPath: data.path } : inv)));
                    if (pending && pending.type === 'invoice' && pending.invoiceId === id) {
                      sendAfterPdfRef.current = null;
                      api.apiSendInvoiceEmail({
                        invoiceId: id,
                        to: pending.customer.email,
                        subject: pending.subject,
                        body: pending.body,
                      })
                        .then(() => {
                          setInvoices((prev) => prev.map((inv) => (inv.id === id ? { ...inv, status: 'sent' } : inv)));
                          api.apiGetSentEmails().then((data) => { if (Array.isArray(data)) setSentEmails(data); }).catch(() => { });
                          showToast('E-Mail mit PDF-Anhang wurde versendet. Status auf "Gesendet" gesetzt.', 'success');
                          showEmailSentConfirmation({ to: pending.customer.email, subject: pending.subject, docType: 'invoice', docId: id });
                        })
                        .catch((err) => {
                          showToast('E-Mail-Versand fehlgeschlagen: ' + (err?.message || err), 'error');
                        });
                    }
                  }
                })
                .catch((err) => {
                  sendAfterPdfRef.current = null;
                  showToast('PDF konnte nicht erstellt werden: ' + (err?.message || err), 'error');
                })
                .finally(() => {
                  setPdfGenData(null);
                });
            }).catch(() => {
              setPdfGenData(null);
              showToast('PDF-Modul konnte nicht geladen werden.', 'error');
            });
          });
        };
        requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(doCapture, 600)));
      });
    }, 2200);
    return () => clearTimeout(timer);
  }, [pdfGenData]);

  const [showSettings, setShowSettings] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isEditingInvoice, setIsEditingInvoice] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [invoiceFilter, setInvoiceFilter] = useState('all');

  const [isEditingQuote, setIsEditingQuote] = useState(false);
  const [currentQuote, setCurrentQuote] = useState(null);

  const [isEditingSubscription, setIsEditingSubscription] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);

  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [viewCustomerId, setViewCustomerId] = useState(null);

  const [isEditingTask, setIsEditingTask] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);

  const [paidModalInvoice, setPaidModalInvoice] = useState(null);
  const [paidDateInput, setPaidDateInput] = useState('');
  const [nextInvoiceId, setNextInvoiceId] = useState(null);

  const [csvDateFrom, setCsvDateFrom] = useState('');
  const [csvDateTo, setCsvDateTo] = useState('');
  const globalSearchRef = useRef(null);
  const sendAfterPdfRef = useRef(null);

  useEffect(() => {
    const onMouseDown = (e) => {
      if (globalSearchRef.current && !globalSearchRef.current.contains(e.target)) setGlobalSearchOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const stats = useMemo(() => {
    const isSmallBusinessStats = companySettings?.isSmallBusiness === true
      || companySettings?.isSmallBusiness === 1
      || companySettings?.isSmallBusiness === '1'
      || companySettings?.isSmallBusiness === 'true';
    const totalRevenue = invoices
      .filter((i) => i.status === 'paid')
      .reduce((acc, inv) => acc + calculateDocumentTotals(inv, { isSmallBusiness: isSmallBusinessStats, includeReminderFee: true }).totalNet, 0);
    const openAmount = invoices
      .filter((i) => i.status === 'sent' || i.status === 'overdue')
      .reduce((acc, inv) => acc + calculateDocumentTotals(inv, { isSmallBusiness: isSmallBusinessStats, includeReminderFee: true }).totalNet, 0);
    const overdueAmount = invoices
      .filter(
        (i) =>
          i.status === 'overdue' ||
          (new Date(i.dueDate) < new Date() && i.status !== 'paid')
      )
      .reduce((acc, inv) => acc + calculateDocumentTotals(inv, { isSmallBusiness: isSmallBusinessStats, includeReminderFee: true }).totalNet, 0);
    const thisMonth = new Date().getMonth();
    const thisYear = new Date().getFullYear();
    const invoiceCountThisMonth = invoices.filter(
      (i) => new Date(i.date).getMonth() === thisMonth && new Date(i.date).getFullYear() === thisYear
    ).length;
    const activeSubscriptionsCount = subscriptions.filter((s) => s.active).length;
    const contractAlertDays = Math.max(1, Number(companySettings.contractAlertDays || 30));
    return { totalRevenue, openAmount, overdueAmount, invoiceCountThisMonth, activeSubscriptionsCount, contractAlertDays };
  }, [invoices, subscriptions, companySettings.contractAlertDays]);

  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const localSearchResults = useMemo(() => {
    const q = (globalSearchQuery || '').trim().toLowerCase();
    if (q.length < 2) return { invoices: [], quotes: [], customers: [], subscriptions: [], tasks: [], notes: [], documents: [] };
    const invs = invoices.filter(
      (i) => i.id.toLowerCase().includes(q) || (customers.find((c) => c.id === i.customerId)?.name || '').toLowerCase().includes(q)
    ).slice(0, 5);
    const quots = quotes.filter(
      (qo) => qo.id.toLowerCase().includes(q) || (customers.find((c) => c.id === qo.customerId)?.name || '').toLowerCase().includes(q)
    ).slice(0, 5);
    const custs = customers.filter(
      (c) => c.name.toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q)
    ).slice(0, 5);
    const subs = subscriptions.filter((s) => (s.description || '').toLowerCase().includes(q) || (s.id || '').toLowerCase().includes(q)).slice(0, 5);
    const tks = tasks.filter((t) => (t.title || '').toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q)).slice(0, 5);
    return { invoices: invs, quotes: quots, customers: custs, subscriptions: subs, tasks: tks, notes: [], documents: [] };
  }, [globalSearchQuery, invoices, quotes, customers, subscriptions, tasks]);
  const [globalSearchResults, setGlobalSearchResults] = useState(localSearchResults);

  useEffect(() => {
    const q = (globalSearchQuery || '').trim();
    if (q.length < 2) {
      setGlobalSearchResults({ invoices: [], quotes: [], customers: [], subscriptions: [], tasks: [], notes: [], documents: [] });
      return;
    }
    let canceled = false;
    const t = setTimeout(async () => {
      if (!hasApi()) {
        setGlobalSearchResults(localSearchResults);
        return;
      }
      try {
        const data = await api.apiSearchAll(q);
        if (!canceled) setGlobalSearchResults({ ...localSearchResults, ...(data || {}) });
      } catch {
        if (!canceled) setGlobalSearchResults(localSearchResults);
      }
    }, 220);
    return () => {
      canceled = true;
      clearTimeout(t);
    };
  }, [globalSearchQuery, localSearchResults]);

  const handleDashboardCardClick = (targetFilter) => {
    setInvoiceFilter(targetFilter);
    setActiveTab('invoices');
  };

  const getCustomerNameById = (id) => customers.find((c) => c.id === id)?.name || 'Unbekannt';
  const isSmallBusiness = companySettings?.isSmallBusiness === true
    || companySettings?.isSmallBusiness === 1
    || companySettings?.isSmallBusiness === '1'
    || companySettings?.isSmallBusiness === 'true';
  const normalizeItemsTaxBySettings = (items = []) => (items || []).map((item) => ({
    ...item,
    taxRate: isSmallBusiness ? 0 : (item?.taxRate !== undefined ? item.taxRate : 0.19),
  }));
  const getNetTotal = (doc, includeReminderFee = false) =>
    calculateDocumentTotals(doc, { isSmallBusiness, includeReminderFee }).totalNet;
  const getGrossTotal = (doc, includeReminderFee = false) =>
    calculateDocumentTotals(doc, { isSmallBusiness, includeReminderFee }).totalGross;

  const handleSaveSettings = async (newSettings) => {
    const normalizeHexColor = (value, fallback = '#2563eb') => {
      const raw = String(value || '').trim();
      if (!raw) return fallback;
      const withHash = raw.startsWith('#') ? raw : `#${raw}`;
      return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(withHash) ? withHash.toLowerCase() : fallback;
    };
    const preparedSettings = {
      ...newSettings,
      primaryColor: normalizeHexColor(newSettings?.primaryColor),
    };
    if (hasApi()) {
      try {
        await api.apiSaveSettings(preparedSettings);
      } catch (err) {
        showToast('Speichern fehlgeschlagen: ' + (err.message || err), 'error');
        return;
      }
    }
    try {
      localStorage.setItem(COMPANY_DISPLAY_KEY, JSON.stringify({ companyName: preparedSettings.companyName, logoUrl: preparedSettings.logoUrl }));
    } catch (_) { }
    setCompanySettings(preparedSettings);
    setShowSettings(false);
  };

  const applyEmailTemplate = (template, vars) => {
    if (!template || !String(template).trim()) return null;
    let s = template;
    Object.entries(vars || {}).forEach(([k, v]) => {
      const safeVal = v == null ? '' : String(v);
      s = s.replace(new RegExp(`\\{${k}\\}`, 'gi'), safeVal);
    });
    return s;
  };

  const buildDocTemplateVars = ({ type, doc, customer, amount }) => {
    const isReminder = type === 'reminder';
    const dueDate = doc?.dueDate ? formatDate(doc.dueDate) : '';
    const expiryDate = doc?.expiryDate ? formatDate(doc.expiryDate) : '';
    return {
      Dokumentnummer: doc?.id || '',
      Rechnungsnummer: doc?.id || '',
      Angebotsnummer: doc?.id || '',
      Datum: formatDate(doc?.date),
      Faelligkeitsdatum: dueDate,
      Fälligkeitsdatum: dueDate,
      GueltigBis: expiryDate,
      'GültigBis': expiryDate,
      Betrag: formatCurrency(amount || 0),
      Kunde: customer?.name || '',
      Firma: companySettings.companyName || '',
      Mahnstufe: isReminder ? String(doc?.reminderLevel || 1) : '',
    };
  };

  const triggerFileDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const openMailProgramWithAttachments = async ({ type, docId, to, subject, bodyText }) => {
    if (!hasApi()) {
      window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyText)}`;
      showToast('E-Mail-Programm ohne Anhaenge geoeffnet (API nicht verfuegbar).', 'info');
      return false;
    }

    const blob = await api.apiDownloadEmailDraft({
      type,
      docId,
      to,
      subject,
      body: bodyText,
    });
    const fileName = `Emailentwurf_${docId}.eml`;
    const file = new File([blob], fileName, { type: 'message/rfc822' });

    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: subject,
            text: bodyText,
            files: [file],
          });
          showToast('E-Mail-Programm/Teilen geoeffnet, Entwurf mit Anhaengen uebergeben.', 'success');
          return true;
        }
      } catch (err) {
        if (err?.name === 'AbortError') return false;
      }
    }

    triggerFileDownload(blob, fileName);
    showToast('E-Mail-Entwurf mit Anhaengen heruntergeladen. Bitte die .eml Datei im E-Mail-Programm oeffnen.', 'info');
    return true;
  };

  const handleSendQuoteEmail = async (quote) => {
    const customer = customers.find((c) => c.id === quote.customerId);
    if (!customer?.email) {
      showToast('Dieser Kunde hat keine E-Mail-Adresse hinterlegt.', 'error');
      return;
    }
    const totalAmount = getGrossTotal(quote, false);
    const vars = buildDocTemplateVars({ type: 'quote', doc: quote, customer, amount: totalAmount });
    const defaultSubject = `Angebot ${quote.id} von ${companySettings.companyName}`;
    const defaultBody = `Guten Tag,\n\nanbei erhalten Sie unser Angebot Nr. ${quote.id} vom ${formatDate(quote.date)}.\n\nGesamtbetrag (brutto): ${formatCurrency(totalAmount)}\nGültig bis: ${formatDate(quote.expiryDate)}\n\nMit freundlichen Grüßen\n${companySettings.owner || companySettings.companyName}`;
    const subject = applyEmailTemplate(companySettings.emailSubjectQuote, vars) || defaultSubject;
    const bodyText = applyEmailTemplate(companySettings.emailTemplateQuote, vars) || defaultBody;

    const openQuoteMailProgram = async () => {
      const showStatusDialog = () => {
        setTimeout(() => {
          showConfirm('Angebot per E-Mail versendet? Status auf "Gesendet" setzen?', () => {
            setQuotes(quotes.map((q) => (q.id === quote.id ? { ...q, status: 'sent' } : q)));
            if (hasApi()) api.apiUpdateQuote({ ...quote, status: 'sent' }).catch(() => { });
          });
        }, 500);
      };
      try {
        const opened = await openMailProgramWithAttachments({
          type: 'quote',
          docId: quote.id,
          to: customer.email,
          subject,
          bodyText,
        });
        if (opened) showStatusDialog();
      } catch (err) {
        showToast('E-Mail-Entwurf konnte nicht erstellt werden: ' + (err?.message || err), 'error');
      }
    };

    if (!hasApi()) {
      openQuoteMailProgram();
      return;
    }

    showConfirm(
      'Wie möchten Sie die E-Mail versenden?\n\n• Mit PDF-Anhang: „E-Mail mit PDF versenden“ (Server verschickt die E-Mail).\n• E-Mail-Programm öffnen: erstellt einen E-Mail-Entwurf mit PDF-Anhang.',
      async () => {
        // Server erzeugt das PDF selbst und verschickt die E-Mail.
        startEmailProgress();
        try {
          await api.apiSendQuoteEmail({ quoteId: quote.id, to: customer.email, subject, body: bodyText });
          setQuotes(quotes.map((q) => (q.id === quote.id ? { ...q, status: 'sent' } : q)));
          showToast('E-Mail mit PDF-Anhang wurde versendet. Status auf "Gesendet" gesetzt.', 'success');
          showEmailSentConfirmation({ to: customer.email, subject, docType: 'quote', docId: quote.id });
        } catch (err) {
          showToast('E-Mail-Versand fehlgeschlagen: ' + (err?.message || err), 'error');
        } finally {
          finishEmailProgress();
        }
      },
      openQuoteMailProgram,
      { confirmLabel: 'E-Mail mit PDF versenden', cancelLabel: 'E-Mail-Programm öffnen (mit Anhang)' }
    );
  };

  const handleSendInvoiceEmail = async (invoice) => {
    const customer = customers.find((c) => c.id === invoice.customerId);
    if (!customer?.email) {
      showToast('Dieser Kunde hat keine E-Mail-Adresse hinterlegt.', 'error');
      return;
    }
    const totalAmount = getGrossTotal(invoice, true);
    const customerFullName = getCustomerFullName(customer);
    const greeting = customerFullName
      ? `Hallo ${customer.salutation === 'Herr' ? 'Herr' : customer.salutation === 'Frau' ? 'Frau' : ''} ${customer.lastName}`
      : `Hallo ${customer.name}`;
    const isReminder = Number(invoice.reminderLevel || 0) > 0;
    const reminderLabel = isReminder ? `${invoice.reminderLevel}. Mahnung` : 'Rechnung';
    const defaultBody = `${greeting},\n\nanbei erhalten Sie Ihre ${isReminder ? `${invoice.reminderLevel}. Mahnung` : `Rechnung Nr. ${invoice.id}`} vom ${formatDate(invoice.date)}.\n\nBitte überweisen Sie den Betrag von ${formatCurrency(totalAmount)} bis zum ${formatDate(invoice.dueDate)}.\n\nMit freundlichen Grüßen\n${companySettings.owner || companySettings.companyName}`;
    const templateVars = buildDocTemplateVars({ type: isReminder ? 'reminder' : 'invoice', doc: invoice, customer, amount: totalAmount });
    const bodyText = applyEmailTemplate(isReminder ? companySettings.emailTemplateReminder : companySettings.emailTemplateInvoice, templateVars) || defaultBody;
    const defaultSubject = `${reminderLabel} ${invoice.id} von ${companySettings.companyName}`;
    const subject = applyEmailTemplate(isReminder ? companySettings.emailSubjectReminder : companySettings.emailSubjectInvoice, templateVars) || defaultSubject;

    const openInvoiceMailProgram = async () => {
      const showStatusDialog = () => {
        setTimeout(() => {
          showConfirm('Wurde die Rechnung versendet? (Status auf "Gesendet" setzen)', () => updateInvoiceStatus(invoice.id, 'sent'));
        }, 1000);
      };
      try {
        const opened = await openMailProgramWithAttachments({
          type: 'invoice',
          docId: invoice.id,
          to: customer.email,
          subject,
          bodyText,
        });
        if (opened) showStatusDialog();
      } catch (err) {
        showToast('E-Mail-Entwurf konnte nicht erstellt werden: ' + (err?.message || err), 'error');
      }
    };

    if (!hasApi()) {
      openInvoiceMailProgram();
      return;
    }

    showConfirm(
      'Wie möchten Sie die E-Mail versenden?\n\n• Mit PDF-Anhang: „E-Mail mit PDF versenden“ (Server verschickt die E-Mail).\n• E-Mail-Programm öffnen: erstellt einen E-Mail-Entwurf mit PDF + E-Rechnung (XML).',
      async () => {
        // Server erzeugt das PDF selbst und verschickt die E-Mail.
        startEmailProgress();
        try {
          await api.apiSendInvoiceEmail({
            invoiceId: invoice.id,
            to: customer.email,
            subject,
            body: bodyText,
          });
          updateInvoiceStatus(invoice.id, 'sent');
          showToast('E-Mail mit PDF-Anhang wurde versendet. Status auf "Gesendet" gesetzt.', 'success');
          showEmailSentConfirmation({ to: customer.email, subject, docType: 'invoice', docId: invoice.id });
        } catch (err) {
          showToast('E-Mail-Versand fehlgeschlagen: ' + (err?.message || err), 'error');
        } finally {
          finishEmailProgress();
        }
      },
      openInvoiceMailProgram,
      { confirmLabel: 'E-Mail mit PDF versenden', cancelLabel: 'E-Mail-Programm öffnen (mit Anhang)' }
    );
  };

  const handleSaveTask = async (taskData) => {
    if (hasApi()) {
      try {
        if (taskData.id) {
          await api.apiUpdateTask(taskData);
          setTasks((prev) => prev.map((t) => (t.id === taskData.id ? taskData : t)));
        } else {
          const newT = await api.apiCreateTask(taskData);
          setTasks((prev) => [newT, ...prev]);
        }
        setIsEditingTask(false);
        setCurrentTask(null);
      } catch (err) {
        showToast(err.message || 'Fehler beim Speichern der Aufgabe.', 'error');
      }
    } else {
      if (taskData.id) {
        setTasks((prev) => prev.map((t) => (t.id === taskData.id ? taskData : t)));
      } else {
        setTasks((prev) => [{ ...taskData, id: Date.now(), completed: 0 }, ...prev]);
      }
      setIsEditingTask(false);
      setCurrentTask(null);
    }
  };

  const handleSaveCustomer = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const tagsRaw = formData.get('tags');
    const tags = tagsRaw ? String(tagsRaw).split(',').map((t) => t.trim()).filter(Boolean) : [];
    const street = formData.get('address_street') || '';
    const zip = formData.get('zip') || '';
    const city = formData.get('city') || '';
    const address2 = formData.get('address2') || '';
    const address = [street, zip, city, address2].filter(Boolean).join('\n');

    const newCustomer = {
      id: currentCustomer ? currentCustomer.id : null,
      name: formData.get('name'),
      salutation: formData.get('salutation'),
      firstName: formData.get('firstName'),
      lastName: formData.get('lastName'),
      email: formData.get('email'),
      address,
      zip,
      city,
      tags,
      category: formData.get('category') || undefined,
      defaultPaymentDays: Math.max(0, parseInt(formData.get('defaultPaymentDays'), 10) || 14),
    };
    const duplicate = customers.find((c) => {
      if (currentCustomer && c.id === currentCustomer.id) return false;
      const sameEmail = (c.email || '').trim().toLowerCase() !== '' && (c.email || '').trim().toLowerCase() === String(newCustomer.email || '').trim().toLowerCase();
      const sameName = (c.name || '').trim().toLowerCase() !== '' && (c.name || '').trim().toLowerCase() === String(newCustomer.name || '').trim().toLowerCase();
      return sameEmail || sameName;
    });
    if (duplicate) {
      showToast(`Mögliche Dublette gefunden: ${duplicate.name}${duplicate.email ? ` (${duplicate.email})` : ''}`, 'error');
      return;
    }
    if (hasApi()) {
      try {
        if (currentCustomer) {
          await api.apiUpdateCustomer({ ...newCustomer, id: currentCustomer.id });
        } else {
          const id = await api.apiCreateCustomer(newCustomer);
          newCustomer.id = id;
        }
      } catch (err) {
        showToast('Speichern fehlgeschlagen: ' + (err.message || err), 'error');
        return;
      }
    } else if (!currentCustomer) {
      newCustomer.id = Date.now();
    }
    if (currentCustomer) {
      setCustomers(customers.map((c) => (c.id === currentCustomer.id ? { ...newCustomer, id: currentCustomer.id } : c)));
    } else {
      setCustomers([...customers, newCustomer]);
    }
    setIsEditingCustomer(false);
    setCurrentCustomer(null);
  };

  const handleDeleteCustomer = (id) => {
    showConfirm('Kunden wirklich löschen?', async () => {
      if (hasApi()) {
        try {
          await api.apiDeleteCustomer(id);
        } catch (err) {
          showToast('Löschen fehlgeschlagen: ' + (err.message || err), 'error');
          return;
        }
      }
      setCustomers(customers.filter((c) => c.id !== id));
    });
  };

  const validateInvoice = (data) => {
    if (!data.customerId) return 'Bitte einen Kunden auswählen.';
    const items = data.items || [];
    if (items.length === 0) return 'Mindestens eine Position erforderlich.';
    const hasPrice = items.some((i) => Number(i.price) > 0);
    if (!hasPrice) return 'Mindestens eine Position mit Preis > 0 erforderlich.';
    if (!data.id || !String(data.id).trim()) return 'Rechnungsnummer fehlt.';
    if (invoices.some((i) => i.id === data.id) && (!currentInvoice || currentInvoice.id !== data.id))
      return 'Diese Rechnungsnummer existiert bereits.';
    return null;
  };

  const validateQuote = (data) => {
    if (!data.customerId) return 'Bitte einen Kunden auswählen.';
    const items = data.items || [];
    if (items.length === 0) return 'Mindestens eine Position erforderlich.';
    if (!data.id || !String(data.id).trim()) return 'Angebotsnummer fehlt.';
    return null;
  };

  const handleSaveInvoice = async (invoiceData, subscriptionOptions = null) => {
    const existingInvoice = invoices.find((i) => i.id === invoiceData?.id);
    if (existingInvoice && existingInvoice.status !== 'draft') {
      showToast(`Rechnung ${existingInvoice.id} kann nicht mehr bearbeitet werden. Letzte Bearbeitung nur im Entwurf möglich.`, 'error');
      setIsEditingInvoice(false);
      setCurrentInvoice(null);
      setNextInvoiceId(null);
      return;
    }
    const err = validateInvoice(invoiceData);
    if (err) { showToast(err, 'error'); return; }
    let docToSave = { ...invoiceData, items: normalizeItemsTaxBySettings(invoiceData.items || []) };
    if (hasApi()) {
      try {
        if (invoices.some((i) => i.id === docToSave.id)) {
          await api.apiUpdateInvoice(docToSave);
        } else {
          const created = await api.apiCreateInvoice(docToSave);
          if (created?.id) docToSave.id = created.id;
        }
        if (subscriptionOptions?.create) {
          const useItems = docToSave.items || [];
          const aboAmount = useItems.reduce(
            (acc, item) => acc + item.price * item.quantity,
            0
          );
          const firstDesc = useItems[0]?.description;
          const newSub = {
            id: `SUB-${Date.now()}`,
            customerId: docToSave.customerId,
            description: firstDesc ? `Abo: ${firstDesc}` : 'Wartungsvertrag',
            amount: aboAmount,
            taxRate: isSmallBusiness ? 0 : 0.19,
            discountType: docToSave.discountType || 'none',
            discountValue: Number(docToSave.discountValue) || 0,
            interval: subscriptionOptions.interval,
            nextDueDate: addInterval(docToSave.date, subscriptionOptions.interval),
            active: true,
          };
          const created = await api.apiCreateSubscription(newSub);
          setSubscriptions((prev) => [...prev, { ...newSub, ...created }]);
        }
      } catch (err) {
        showToast('Speichern fehlgeschlagen: ' + (err.message || err), 'error');
        return;
      }
    }
    const exists = invoices.some((i) => i.id === docToSave.id);
    if (exists) {
      setInvoices(invoices.map((i) => (i.id === docToSave.id ? docToSave : i)));
    } else {
      setInvoices([docToSave, ...invoices]);
    }
    if (subscriptionOptions?.create && !hasApi()) {
      const useItems = docToSave.items || [];
      const aboAmount = useItems.reduce(
        (acc, item) => acc + item.price * item.quantity,
        0
      );
      const firstDesc = useItems[0]?.description;
      setSubscriptions((prev) => [
        ...prev,
        {
          id: `SUB-${Date.now()}`,
          customerId: docToSave.customerId,
          description: firstDesc ? `Abo: ${firstDesc}` : 'Wartungsvertrag',
          amount: aboAmount,
          taxRate: isSmallBusiness ? 0 : 0.19,
          discountType: docToSave.discountType || 'none',
          discountValue: Number(docToSave.discountValue) || 0,
          interval: subscriptionOptions.interval,
          nextDueDate: addInterval(docToSave.date, subscriptionOptions.interval),
          active: true,
        },
      ]);
    }
    if (subscriptionOptions?.create) {
      showToast('Rechnung gespeichert. Der Abo-Anteil wurde unter „Abos“ angelegt.', 'success');
      setActiveTab('subscriptions');
    }
    setIsEditingInvoice(false);
    setCurrentInvoice(null);
    setNextInvoiceId(null);
    if (hasApi()) {
      const customer = customers.find((c) => c.id === docToSave.customerId);
      if (customer) setPdfGenData({ type: 'invoice', doc: docToSave, customer, settings: companySettings });
    }
  };

  const updateInvoiceStatus = async (id, newStatus, paidDate = null) => {
    const inv = invoices.find((i) => i.id === id);
    if (!inv) return;
    const updated = {
      ...inv,
      status: newStatus,
      ...(paidDate && { paidDate }),
      ...(newStatus === 'paid' && inv.payments !== undefined ? {} : {}),
    };
    if (hasApi()) {
      try {
        await api.apiUpdateInvoice(updated);
      } catch (err) {
        showToast('Aktualisieren fehlgeschlagen: ' + (err.message || err), 'error');
        return;
      }
    }
    setInvoices(invoices.map((i) => (i.id === id ? updated : i)));
    setPaidModalInvoice(null);
    setPaidDateInput('');
  };

  const openMarkPaidModal = (inv) => {
    setPaidModalInvoice(inv);
    setPaidDateInput(new Date().toISOString().split('T')[0]);
  };

  const getLocalNextInvoiceId = (list = [], date = new Date()) => {
    const year = date.getFullYear();
    const regex = new RegExp(`^RE-${year}-(\\d+)$`);
    let max = 0;
    for (const row of list || []) {
      const id = String(row?.id || '');
      const m = id.match(regex);
      if (!m) continue;
      const n = Number(m[1]);
      if (Number.isFinite(n) && n > max) max = n;
    }
    return `RE-${year}-${max + 1}`;
  };

  const getLocalNextCreditId = (list = [], date = new Date()) => {
    const year = date.getFullYear();
    const regex = new RegExp(`^GS-${year}-(\\d+)$`);
    let max = 0;
    for (const row of list || []) {
      const id = String(row?.id || '');
      const m = id.match(regex);
      if (!m) continue;
      const n = Number(m[1]);
      if (Number.isFinite(n) && n > max) max = n;
    }
    return `GS-${year}-${max + 1}`;
  };

  const getLocalNextStornoId = (list = [], date = new Date()) => {
    const year = date.getFullYear();
    const regex = new RegExp(`^ST-${year}-(\\d+)$`);
    let max = 0;
    for (const row of list || []) {
      const id = String(row?.id || '');
      const m = id.match(regex);
      if (!m) continue;
      const n = Number(m[1]);
      if (Number.isFinite(n) && n > max) max = n;
    }
    return `ST-${year}-${max + 1}`;
  };

  const getLocalNextQuoteId = (list = [], date = new Date()) => {
    const year = date.getFullYear();
    const regex = new RegExp(`^ANG-${year}-(\\d+)$`);
    let max = 0;
    for (const row of list || []) {
      const id = String(row?.id || '');
      const m = id.match(regex);
      if (!m) continue;
      const n = Number(m[1]);
      if (Number.isFinite(n) && n > max) max = n;
    }
    return `ANG-${year}-${max + 1}`;
  };

  const getNextInvoiceId = async () => {
    if (hasApi()) {
      try {
        const d = await api.apiGetNextInvoiceNumber();
        return d.id;
      } catch {
        return getLocalNextInvoiceId(invoices);
      }
    }
    return getLocalNextInvoiceId(invoices);
  };

  const getNextCreditId = async () => {
    if (hasApi()) {
      try {
        const d = await api.apiGetNextCreditNumber();
        return d.id;
      } catch {
        return getLocalNextCreditId(invoices);
      }
    }
    return getLocalNextCreditId(invoices);
  };

  const getNextStornoId = async () => {
    if (hasApi()) {
      try {
        const d = await api.apiGetNextStornoNumber();
        return d.id;
      } catch {
        return getLocalNextStornoId(invoices);
      }
    }
    return getLocalNextStornoId(invoices);
  };

  const getNextQuoteId = async () => {
    if (hasApi()) {
      try {
        const d = await api.apiGetNextQuoteNumber();
        return d.id;
      } catch {
        return getLocalNextQuoteId(quotes);
      }
    }
    return getLocalNextQuoteId(quotes);
  };

  const openNewInvoiceForm = async () => {
    const id = await getNextInvoiceId();
    setNextInvoiceId(id);
    setActiveTab('invoices');
    setCurrentInvoice({ id });
    setIsEditingInvoice(true);
  };

  const openNewQuoteForm = async () => {
    const id = await getNextQuoteId();
    setActiveTab('quotes');
    setCurrentQuote({ id });
    setIsEditingQuote(true);
  };

  const openNewCustomerForm = () => {
    setActiveTab('customers');
    setTimeout(() => {
      setViewCustomerId(null);
      setCurrentCustomer(null);
      setIsEditingCustomer(true);
    }, 50);
  };

  const openNewSubscriptionForm = () => {
    setActiveTab('subscriptions');
    setTimeout(() => {
      setCurrentSubscription(null);
      setIsEditingSubscription(true);
    }, 50);
  };

  const handleDuplicateInvoice = async (inv) => {
    const customer = customers.find((c) => c.id === inv.customerId);
    const newId = await getNextInvoiceId();
    const today = new Date().toISOString().split('T')[0];
    const paymentDays = (customer?.defaultPaymentDays ?? 14) || 14;
    const newInv = {
      ...inv,
      id: newId,
      date: today,
      dueDate: addDays(today, paymentDays),
      status: 'draft',
      reminderLevel: 0,
      reminderFee: 0,
      paidDate: null,
      payments: [],
      pdfPath: null,
      discountType: inv.discountType || 'none',
      discountValue: Number(inv.discountValue) || 0,
      items: normalizeItemsTaxBySettings(inv.items || []),
    };
    setNextInvoiceId(newId);
    setActiveTab('invoices');
    setCurrentInvoice(newInv);
    setIsEditingInvoice(true);
  };

  const createCreditOrStorno = async (inv, mode = 'credit') => {
    const customer = customers.find((c) => c.id === inv.customerId);
    if (!customer) {
      showToast('Kunde zur Rechnung nicht gefunden.', 'error');
      return;
    }
    const newId = mode === 'credit'
      ? await getNextCreditId()
      : mode === 'storno'
        ? await getNextStornoId()
        : await getNextInvoiceId();
    const today = new Date().toISOString().split('T')[0];
    const paymentDays = Number(customer?.defaultPaymentDays ?? 0) || 0;
    const negativeItems = (inv.items || []).map((item) => ({
      ...item,
      price: -Math.abs(Number(item.price) || 0),
      quantity: Number(item.quantity) || 1,
    }));
    const newDoc = {
      id: newId,
      customerId: inv.customerId,
      date: today,
      dueDate: addDays(today, paymentDays),
      status: 'draft',
      reminderLevel: 0,
      reminderFee: 0,
      paidDate: null,
      payments: [],
      pdfPath: null,
      items: negativeItems,
      discountType: inv.discountType || 'none',
      discountValue: Number(inv.discountValue) || 0,
      note: mode === 'storno'
        ? `Storno zu Rechnung ${inv.id}`
        : `Gutschrift zu Rechnung ${inv.id}`,
    };

    if (hasApi()) {
      try {
        await api.apiCreateInvoice(newDoc);
        if (mode === 'storno' || mode === 'credit') {
          await api.apiUpdateInvoice({ ...inv, status: mode === 'storno' ? 'cancelled' : 'credited' });
        }
      } catch (err) {
        showToast('Fehler beim Erstellen: ' + (err?.message || err), 'error');
        return;
      }
    }

    setInvoices((prev) => {
      const withNew = [newDoc, ...prev];
      if (mode === 'storno') return withNew.map((i) => (i.id === inv.id ? { ...i, status: 'cancelled' } : i));
      if (mode === 'credit') return withNew.map((i) => (i.id === inv.id ? { ...i, status: 'credited' } : i));
      return withNew;
    });
    showToast(mode === 'storno' ? `Storno ${newId} erstellt.` : `Gutschrift ${newId} erstellt.`, 'success');
    setActiveTab('invoices');
  };

  const createInstallmentPlan = async (inv, parts = 3) => {
    const count = Math.max(2, Number(parts) || 3);
    const customer = customers.find((c) => c.id === inv.customerId);
    if (!customer) {
      showToast('Kunde zur Rechnung nicht gefunden.', 'error');
      return;
    }
    const total = getNetTotal(inv, true);
    if (total <= 0) {
      showToast('Für Ratenplan wird eine positive Rechnungssumme benötigt.', 'error');
      return;
    }

    const baseRate = Math.floor((total / count) * 100) / 100;
    let remainder = Number((total - baseRate * count).toFixed(2));
    const created = [];
    for (let idx = 0; idx < count; idx++) {
      const newId = await getNextInvoiceId();
      const rateAmount = Number((baseRate + (idx === count - 1 ? remainder : 0)).toFixed(2));
      const date = new Date(inv.date || new Date());
      date.setMonth(date.getMonth() + idx);
      const dateIso = date.toISOString().split('T')[0];
      const dueDate = addDays(dateIso, Number(customer.defaultPaymentDays || 14));
      const doc = {
        id: newId,
        customerId: inv.customerId,
        date: dateIso,
        dueDate,
        status: 'draft',
        reminderLevel: 0,
        reminderFee: 0,
        paidDate: null,
        payments: [],
        pdfPath: null,
        note: `Rate ${idx + 1}/${count} zu Rechnung ${inv.id}`,
        items: [{
          description: `Ratenzahlung ${idx + 1}/${count} zu ${inv.id}`,
          quantity: 1,
          price: rateAmount,
          taxRate: isSmallBusiness ? 0 : 0.19,
        }],
      };
      if (hasApi()) {
        try {
          await api.apiCreateInvoice(doc);
        } catch (e) {
          showToast('Ratenplan konnte nicht vollständig erstellt werden: ' + (e?.message || e), 'error');
          break;
        }
      }
      created.push(doc);
    }
    if (created.length > 0) {
      setInvoices((prev) => [...created, ...prev]);
      showToast(`${created.length} Ratenrechnungen erstellt.`, 'success');
    }
  };

  const handleDeleteInvoiceDraft = (invoice) => {
    const id = String(invoice?.id || '');
    if (!id) return;
    if ((invoice?.status || '') !== 'draft') {
      showToast('Nur Rechnungsentwürfe können gelöscht werden.', 'error');
      return;
    }

    showConfirm(`Entwurf ${id} wirklich löschen?`, async () => {
      if (hasApi()) {
        try {
          await api.apiDeleteInvoice(id);
        } catch (err) {
          showToast('Löschen fehlgeschlagen: ' + (err?.message || err), 'error');
          return;
        }
      }
      setInvoices((prev) => prev.filter((i) => i.id !== id));
      if (currentInvoice?.id === id) {
        setCurrentInvoice(null);
        setIsEditingInvoice(false);
      }
    });
  };

  const handleDuplicateQuote = async (quote) => {
    const newId = await getNextQuoteId();
    const today = new Date().toISOString().split('T')[0];
    const newQuote = {
      ...quote,
      id: newId,
      date: today,
      expiryDate: addDays(today, 30),
      status: 'sent',
      pdfPath: null,
      items: normalizeItemsTaxBySettings(quote.items || []),
    };
    setActiveTab('quotes');
    setCurrentQuote(newQuote);
    setIsEditingQuote(true);
  };

  const handleQuoteToInvoice = async (quote) => {
    const customer = customers.find((c) => c.id === quote.customerId);
    if (!customer) { showToast('Kunde zum Angebot nicht gefunden.', 'error'); return; }
    const newId = await getNextInvoiceId();
    const today = new Date().toISOString().split('T')[0];
    const paymentDays = (customer?.defaultPaymentDays ?? 14) || 14;
    const newInv = {
      id: newId,
      customerId: quote.customerId,
      date: today,
      dueDate: addDays(today, paymentDays),
      status: 'draft',
      reminderLevel: 0,
      items: normalizeItemsTaxBySettings(quote.items || []),
      pdfPath: null,
      payments: [],
      reminderFee: 0,
      discountType: quote.discountType || 'none',
      discountValue: Number(quote.discountValue) || 0,
    };
    if (hasApi()) {
      try {
        await api.apiCreateInvoice(newInv);
        await api.apiUpdateQuote({ ...quote, status: 'accepted' });
      } catch (err) {
        showToast('Rechnung anlegen fehlgeschlagen: ' + (err?.message || err), 'error');
        return;
      }
    }
    setInvoices([newInv, ...invoices]);
    setQuotes(quotes.map((q) => (q.id === quote.id ? { ...q, status: 'accepted' } : q)));
    setCurrentQuote(null);
    setIsEditingQuote(false);
    setActiveTab('invoices');
    setNextInvoiceId(newId);
    setCurrentInvoice(newInv);
    setIsEditingInvoice(true);
  };

  const csvEscape = (value) => {
    const s = String(value ?? '');
    if (/[",;\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const filterInvoicesByDate = (dateFrom = null, dateTo = null) => {
    let list = invoices;
    if (!dateFrom && !dateTo) return list;
    list = invoices.filter((inv) => {
      const d = new Date(inv.date);
      if (dateFrom && d < new Date(dateFrom)) return false;
      if (dateTo && d > new Date(dateTo + 'T23:59:59')) return false;
      return true;
    });
    return list;
  };

  const triggerCsvDownload = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportToCSV = (dateFrom = null, dateTo = null) => {
    const list = filterInvoicesByDate(dateFrom, dateTo);
    const headers = ['ID', 'Kunde', 'Datum', 'Fällig', 'Netto', 'Status'];
    const rows = list.map((inv) => {
      const customer = customers.find((c) => c.id === inv.customerId);
      const total = getNetTotal(inv, true);
      return [inv.id, customer?.name || 'Unbekannt', inv.date, inv.dueDate, total.toFixed(2), inv.status].map(csvEscape).join(',');
    });
    triggerCsvDownload([headers.join(','), ...rows].join('\n'), `rechnungen_export_${dateFrom || 'alle'}_${dateTo || 'alle'}.csv`.replace(/\//g, '-'));
  };

  const exportDatevCsv = (dateFrom = null, dateTo = null) => {
    const list = filterInvoicesByDate(dateFrom, dateTo);
    const delimiter = String(companySettings.datevDelimiter || ';') === ',' ? ',' : ';';
    const headers = ['Belegdatum', 'Belegfeld 1', 'Konto', 'Gegenkonto', 'Buchungstext', 'Umsatz (ohne Soll/Haben-Kz)', 'Steuerschlüssel', 'Kunde', 'Fälligkeit', 'Status'];
    const rows = list.map((inv) => {
      const customer = customers.find((c) => c.id === inv.customerId);
      const netTotal = getNetTotal(inv, true);
      const firstTaxRate = Number(inv?.items?.[0]?.taxRate);
      const steuerschluessel = firstTaxRate === 0.07 ? '2' : firstTaxRate === 0 ? '0' : '1';
      const konto = firstTaxRate === 0.07
        ? (companySettings.datevRevenueAccount7 || companySettings.datevRevenueAccount || '8300')
        : firstTaxRate === 0
          ? (companySettings.datevRevenueAccount0 || companySettings.datevRevenueAccount || '8125')
          : (companySettings.datevRevenueAccount19 || companySettings.datevRevenueAccount || '8400');
      const row = [
        inv.date || '',
        inv.id || '',
        konto,
        companySettings.datevDebitorAccount || '10000',
        `Rechnung ${inv.id}`,
        netTotal.toFixed(2),
        steuerschluessel,
        customer?.name || '',
        inv.dueDate || '',
        inv.status || '',
      ];
      return row.map(csvEscape).join(delimiter);
    });
    const profile = (companySettings.datevProfileName || 'standard').toLowerCase().replace(/[^a-z0-9_-]/gi, '');
    const filename = `datev_export_${profile}_${dateFrom || 'alle'}_${dateTo || 'alle'}.csv`.replace(/\//g, '-');
    triggerCsvDownload([headers.map(csvEscape).join(delimiter), ...rows].join('\n'), filename);
  };

  const exportBackup = () => {
    const data = {
      version: 1,
      exportedAt: new Date().toISOString(),
      companySettings,
      customers,
      invoices,
      quotes,
      subscriptions,
      tasks,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `devsuite-backup-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const importBackup = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const data = JSON.parse(ev.target?.result);
          if (!data.customers && !data.invoices) throw new Error('Ungültiges Backup-Format');
          showConfirm('Alle aktuellen Daten durch die Backup-Datei ersetzen? (Mit API werden Daten nur lokal überschrieben.)', () => {
            if (data.companySettings) setCompanySettings(data.companySettings);
            if (Array.isArray(data.customers)) setCustomers(data.customers);
            if (Array.isArray(data.invoices)) setInvoices(data.invoices);
            if (Array.isArray(data.quotes)) setQuotes(data.quotes);
            if (Array.isArray(data.subscriptions)) setSubscriptions(data.subscriptions);
            if (Array.isArray(data.tasks)) setTasks(data.tasks);
            showToast('Backup importiert. Bei Nutzung der API bitte ggf. Server-Daten separat aktualisieren.', 'success');
          });
        } catch (err) {
          showToast('Import fehlgeschlagen: ' + (err.message || 'Ungültige Datei'), 'error');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const handleSaveQuote = async (quoteData) => {
    const err = validateQuote(quoteData);
    if (err) { showToast(err, 'error'); return; }
    let docToSave = { ...quoteData, items: normalizeItemsTaxBySettings(quoteData.items || []) };
    if (hasApi()) {
      try {
        if (quotes.some((q) => q.id === docToSave.id)) {
          await api.apiUpdateQuote(docToSave);
        } else {
          const created = await api.apiCreateQuote(docToSave);
          if (created?.id) docToSave.id = created.id;
        }
      } catch (err) {
        showToast('Speichern fehlgeschlagen: ' + (err.message || err), 'error');
        return;
      }
    }
    const quoteExists = quotes.some((q) => q.id === docToSave.id);
    if (quoteExists) {
      setQuotes(quotes.map((q) => (q.id === docToSave.id ? docToSave : q)));
    } else {
      setQuotes([docToSave, ...quotes]);
    }
    setIsEditingQuote(false);
    setCurrentQuote(null);
    if (hasApi()) {
      const customer = customers.find((c) => c.id === docToSave.customerId);
      if (customer) setPdfGenData({ type: 'quote', doc: docToSave, customer, settings: companySettings });
    }
  };

  const handleDeleteQuote = (quote) => {
    const id = String(quote?.id || '');
    if (!id) return;
    showConfirm(`Angebot ${id} wirklich löschen?`, async () => {
      if (hasApi()) {
        try {
          await api.apiDeleteQuote(id);
        } catch (err) {
          showToast('Löschen fehlgeschlagen: ' + (err?.message || err), 'error');
          return;
        }
      }
      setQuotes((prev) => prev.filter((q) => q.id !== id));
      if (currentQuote?.id === id) {
        setCurrentQuote(null);
        setIsEditingQuote(false);
      }
    });
  };

  const handleSaveSubscription = async (subData) => {
    if (hasApi()) {
      try {
        if (currentSubscription) {
          await api.apiUpdateSubscription(subData);
        } else {
          await api.apiCreateSubscription(subData);
        }
      } catch (err) {
        showToast('Speichern fehlgeschlagen: ' + (err.message || err), 'error');
        return;
      }
    }
    if (currentSubscription) {
      setSubscriptions(subscriptions.map((s) => (s.id === currentSubscription.id ? subData : s)));
    } else {
      setSubscriptions([subData, ...subscriptions]);
    }
    setIsEditingSubscription(false);
    setCurrentSubscription(null);
  };

  const handleDeleteSubscription = (id) => {
    showConfirm('Abo wirklich löschen?', async () => {
      if (hasApi()) {
        try {
          await api.apiDeleteSubscription(id);
        } catch (err) {
          showToast('Löschen fehlgeschlagen: ' + (err.message || err), 'error');
          return;
        }
      }
      setSubscriptions(subscriptions.filter((s) => s.id !== id));
      setIsEditingSubscription(false);
    });
  };

  const handleOpenSentEmailDocument = (row) => {
    const docType = String(row?.documentType || '').toLowerCase();
    const docId = String(row?.documentId || '');
    if (!docType || !docId) return;

    if (docType === 'invoice') {
      const inv = invoices.find((i) => String(i.id) === docId);
      if (!inv) {
        showToast(`Rechnung ${docId} wurde nicht gefunden.`, 'error');
        return;
      }
      setActiveTab('invoices');
      if (inv.status !== 'draft') {
        showToast(`Rechnung ${docId} ist bereits versendet und kann nicht mehr bearbeitet werden.`, 'info');
        return;
      }
      setCurrentInvoice(inv);
      setIsEditingInvoice(true);
      return;
    }

    if (docType === 'quote') {
      const quote = quotes.find((q) => String(q.id) === docId);
      if (!quote) {
        showToast(`Angebot ${docId} wurde nicht gefunden.`, 'error');
        return;
      }
      setActiveTab('quotes');
      setCurrentQuote(quote);
      setIsEditingQuote(true);
      return;
    }

    showToast('Unbekannter Belegtyp in E-Mail-Historie.', 'error');
  };

  const runSubscription = async (sub) => {
    if (sub.contractEndDate && new Date(sub.contractEndDate) < new Date() && sub.autoRenew === false) {
      showToast('Vertrag ist abgelaufen (ohne Auto-Verlängerung).', 'info');
      return;
    }
    const newId = await getNextInvoiceId();
    const newInvoice = {
      id: newId,
      customerId: sub.customerId,
      date: new Date().toISOString().split('T')[0],
      dueDate: addDays(new Date(), 14),
      items: [{
        description: sub.description,
        quantity: 1,
        price: sub.amount,
        taxRate: isSmallBusiness ? 0 : (sub.taxRate !== undefined ? sub.taxRate : 0.19),
      }],
      status: 'sent',
      reminderLevel: 0,
      reminderFee: 0,
      discountType: sub.discountType || 'none',
      discountValue: Number(sub.discountValue) || 0,
    };
    const updatedSub = { ...sub, nextDueDate: addInterval(sub.nextDueDate, sub.interval) };
    if (hasApi()) {
      try {
        await api.apiCreateInvoice(newInvoice);
        await api.apiUpdateSubscription(updatedSub);
      } catch (err) {
        showToast('Fehler: ' + (err.message || err), 'error');
        return;
      }
    }
    setInvoices([newInvoice, ...invoices]);
    setSubscriptions(subscriptions.map((s) => (s.id === sub.id ? updatedSub : s)));
    showToast(`Abo-Rechnung ${newInvoice.id} erstellt.`, 'success');
  };

  const filterInvoices = (inv) => {
    const isOverdue = new Date(inv.dueDate) < new Date() && inv.status !== 'paid';
    const displayStatus = isOverdue ? 'overdue' : inv.status;
    const isReminder = Number(inv?.reminderLevel || 0) > 0;
    const isCredit = String(inv?.id || '').startsWith('G-') || /^gutschrift\s+zu/i.test(String(inv?.note || ''));
    if (invoiceFilter === 'all') return true;
    if (invoiceFilter === 'paid') return inv.status === 'paid';
    if (invoiceFilter === 'open')
      return inv.status === 'sent' || inv.status === 'overdue' || isOverdue;
    if (invoiceFilter === 'overdue') return displayStatus === 'overdue';
    if (invoiceFilter === 'reminders') return isReminder;
    if (invoiceFilter === 'credits') return isCredit;
    return true;
  };

  const handleForgotPassword = async () => {
    setForgotState((s) => ({ ...s, loading: true, error: '', message: '' }));
    try {
      const res = await fetch(`${API_BASE_URL}/forgot-password.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotState.email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Anfrage fehlgeschlagen.');
      setForgotState((s) => ({
        ...s,
        loading: false,
        message: data?.message || 'Wenn ein Konto existiert, wurde eine E-Mail versendet.',
      }));
    } catch (e) {
      setForgotState((s) => ({ ...s, loading: false, error: e.message || 'Fehler bei der Anfrage.' }));
    }
  };

  const handleResetPassword = async () => {
    setResetState((s) => ({ ...s, loading: true, error: '', message: '' }));
    try {
      const res = await fetch(`${API_BASE_URL}/reset-password.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: resetToken,
          password: resetState.password,
          confirmPassword: resetState.confirmPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Zuruecksetzen fehlgeschlagen.');
      setResetState((s) => ({
        ...s,
        loading: false,
        message: data?.message || 'Passwort wurde gesetzt. Du kannst dich jetzt anmelden.',
      }));
    } catch (e) {
      setResetState((s) => ({ ...s, loading: false, error: e.message || 'Zuruecksetzen fehlgeschlagen.' }));
    }
  };

  const finalizeClientLogin = (rawUser) => {
    if (!rawUser || typeof rawUser !== 'object') throw new Error('Login fehlgeschlagen');
    const normalized = { ...rawUser };
    normalized.is_admin = normalized.is_admin === 1 || normalized.is_admin === '1' || normalized.is_admin === true;
    if (!normalized.company_id && !normalized.is_admin) throw new Error('Keine company_id im Benutzerkonto hinterlegt.');
    localStorage.setItem(COMPANY_ID_KEY, String(normalized.company_id || ''));
    localStorage.setItem('devsuite_user', JSON.stringify(normalized));
    window.location.reload();
  };

  const handlePasswordLogin = async () => {
    setLoginState((s) => ({ ...s, loading: true, error: '' }));
    setTwoFactorState((s) => ({ ...s, error: '' }));
    try {
      const res = await fetch(`${API_BASE_URL}/login.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginState.email, password: loginState.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || data?.message || 'Login fehlgeschlagen');
      if (data?.twoFactorRequired && data?.challengeToken) {
        setTwoFactorState({
          challengeToken: data.challengeToken,
          emailMasked: data.emailMasked || loginState.email,
          code: '',
          trustDevice: true,
          expiresAt: Date.now() + (Number(data.expiresIn) || 600) * 1000,
          loading: false,
          resendLoading: false,
          error: '',
        });
        setLoginState((s) => ({ ...s, loading: false, error: '' }));
        return;
      }
      if (!data?.user) throw new Error('Login fehlgeschlagen');
      finalizeClientLogin(data.user);
    } catch (err) {
      setLoginState((s) => ({ ...s, loading: false, error: err?.message || 'Login fehlgeschlagen' }));
    }
  };

  const handleVerifyTwoFactor = async () => {
    if (!twoFactorState.challengeToken) return;
    setTwoFactorState((s) => ({ ...s, loading: true, error: '' }));
    try {
      const res = await fetch(`${API_BASE_URL}/2fa-verify.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          challengeToken: twoFactorState.challengeToken,
          code: twoFactorState.code,
          trustDevice: !!twoFactorState.trustDevice,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || data?.message || '2FA-Verifizierung fehlgeschlagen');
      if (!data?.user) throw new Error('Login fehlgeschlagen');
      finalizeClientLogin(data.user);
    } catch (err) {
      setTwoFactorState((s) => ({ ...s, loading: false, error: err?.message || '2FA-Verifizierung fehlgeschlagen' }));
    }
  };

  const handleResendTwoFactor = async () => {
    if (!twoFactorState.challengeToken) return;
    setTwoFactorState((s) => ({ ...s, resendLoading: true, error: '' }));
    try {
      const res = await fetch(`${API_BASE_URL}/2fa-resend.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challengeToken: twoFactorState.challengeToken }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || data?.message || 'Code konnte nicht erneut gesendet werden');
      setTwoFactorState((s) => ({
        ...s,
        resendLoading: false,
        expiresAt: Date.now() + (Number(data?.expiresIn) || 600) * 1000,
        emailMasked: data?.emailMasked || s.emailMasked,
        error: '',
      }));
      showToast('Neuer Sicherheitscode wurde per E-Mail gesendet.', 'success');
    } catch (err) {
      setTwoFactorState((s) => ({ ...s, resendLoading: false, error: err?.message || 'Code konnte nicht erneut gesendet werden' }));
    }
  };

  const renderLoginForm = () => {
    if (twoFactorState.challengeToken) {
      return (
        <>
          <p className="mb-6 text-sm leading-relaxed" style={{ color: 'var(--nb-muted)' }}>
            Sicherheitscode eingeben. Wir haben einen 6-stelligen Code an <strong>{twoFactorState.emailMasked || 'deine E-Mail'}</strong> gesendet.
          </p>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            placeholder="123456"
            className="mb-3 w-full rounded-2xl border bg-white px-4 py-3 text-[16px] tracking-[0.3em] text-center transition-colors focus:outline-none focus:ring-2"
            style={{ borderColor: 'var(--nb-border)' }}
            value={twoFactorState.code}
            onChange={(e) => setTwoFactorState((s) => ({ ...s, code: String(e.target.value || '').replace(/\D/g, '').slice(0, 6) }))}
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') document.getElementById('verify2faBtn')?.click(); }}
          />
          <label className="mb-2 flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={!!twoFactorState.trustDevice}
              onChange={(e) => setTwoFactorState((s) => ({ ...s, trustDevice: !!e.target.checked }))}
            />
            2FA auf diesem Geraet 7 Tage nicht erneut abfragen
          </label>
          <button
            id="verify2faBtn"
            className="mt-3 w-full rounded-full px-6 py-3.5 text-[16px] font-semibold text-white shadow-sm transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-50 touch-manipulation"
            style={{ backgroundColor: 'var(--nb-primary)' }}
            disabled={twoFactorState.loading || (twoFactorState.code || '').length !== 6}
            onClick={handleVerifyTwoFactor}
          >
            {twoFactorState.loading ? 'Pruefe…' : 'Code bestaetigen'}
          </button>
          <button
            type="button"
            className="mt-3 w-full rounded-full px-4 py-2.5 text-sm font-semibold border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
            disabled={twoFactorState.resendLoading}
            onClick={handleResendTwoFactor}
          >
            {twoFactorState.resendLoading ? 'Sende…' : 'Code erneut senden'}
          </button>
          <p className="mt-3 text-xs text-slate-500">Code ist ca. 10 Minuten gueltig.</p>
          {twoFactorState.error && <div className="mt-3 text-center text-sm font-medium text-[#FF3B30]">{twoFactorState.error}</div>}
          <button
            type="button"
            className="mt-4 text-sm font-semibold hover:underline"
            style={{ color: 'var(--nb-primary)' }}
            onClick={() => {
              setTwoFactorState({
                challengeToken: '',
                emailMasked: '',
                code: '',
                trustDevice: true,
                expiresAt: 0,
                loading: false,
                resendLoading: false,
                error: '',
              });
            }}
          >
            Zurueck zum Login
          </button>
        </>
      );
    }

    return (
      <>
        <p className="mb-6 text-sm leading-relaxed" style={{ color: 'var(--nb-muted)' }}>Melde dich mit deiner E-Mail und deinem Passwort an.</p>
        <input
          type="email"
          placeholder="E-Mail"
          className="mb-3 w-full rounded-2xl border bg-white px-4 py-3 text-[16px] transition-colors focus:outline-none focus:ring-2"
          style={{ borderColor: 'var(--nb-border)' }}
          value={loginState.email}
          onChange={e => setLoginState(s => ({ ...s, email: e.target.value }))}
          autoFocus
        />
        <input
          type="password"
          placeholder="Passwort"
          className="mb-2 w-full rounded-2xl border bg-white px-4 py-3 text-[16px] transition-colors focus:outline-none focus:ring-2"
          style={{ borderColor: 'var(--nb-border)' }}
          value={loginState.password}
          onChange={e => setLoginState(s => ({ ...s, password: e.target.value }))}
          onKeyDown={e => { if (e.key === 'Enter') document.getElementById('loginBtn')?.click(); }}
        />
        <button
          id="loginBtn"
          className="mt-5 w-full rounded-full px-6 py-3.5 text-[16px] font-semibold text-white shadow-sm transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-50 touch-manipulation"
          style={{ backgroundColor: 'var(--nb-primary)' }}
          disabled={loginState.loading}
          onClick={handlePasswordLogin}
        >
          {loginState.loading ? 'Anmeldung…' : 'Anmelden'}
        </button>
        {loginState.error && <div className="mt-4 text-center text-sm font-medium text-[#FF3B30]">{loginState.error}</div>}
        <button
          type="button"
          className="mt-4 text-sm font-semibold hover:underline"
          style={{ color: 'var(--nb-primary)' }}
          onClick={() => {
            setShowForgotPassword((v) => !v);
            setForgotState((s) => ({ ...s, email: s.email || loginState.email, message: '', error: '' }));
          }}
        >
          Passwort vergessen?
        </button>
        {showForgotPassword && (
          <div className="mt-4 w-full rounded-2xl border p-3" style={{ borderColor: 'var(--nb-border)', backgroundColor: 'var(--nb-surface-soft)' }}>
            <input
              type="email"
              placeholder="E-Mail fuer Reset"
              className="w-full rounded-xl border bg-white px-3 py-2 text-sm focus:outline-none"
              style={{ borderColor: 'var(--nb-border)' }}
              value={forgotState.email}
              onChange={(e) => setForgotState((s) => ({ ...s, email: e.target.value }))}
            />
            <button
              type="button"
              className="mt-3 w-full rounded-full px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              style={{ backgroundColor: 'var(--nb-primary)' }}
              disabled={forgotState.loading || !forgotState.email}
              onClick={handleForgotPassword}
            >
              {forgotState.loading ? 'Sende…' : 'Reset-Link senden'}
            </button>
            {forgotState.error && <div className="mt-2 text-center text-xs font-medium text-[#FF3B30]">{forgotState.error}</div>}
            {forgotState.message && <div className="mt-2 text-center text-xs font-medium text-emerald-600">{forgotState.message}</div>}
          </div>
        )}
      </>
    );
  };


  // ... (Hooks and Handlers moved up) ...

  // Conditional Rendering Logic moved to the end
  if (resetToken) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 py-10 font-sans" style={{ background: 'radial-gradient(1200px 420px at 10% -10%, #dfe9ff 0, transparent 70%), var(--nb-bg)' }}>
        <div className="nb-card w-full max-w-[460px] p-8 md:p-10">
          <div className="mb-7 flex items-center gap-4">
            <img src="/Nextbill Icon.png" alt="Logo" className="h-16 w-16 rounded-2xl border object-contain p-2" style={{ borderColor: 'var(--nb-border)', backgroundColor: 'var(--nb-surface-soft)' }} />
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--nb-muted)' }}>NextBill</p>
              <h2 className="text-[24px] font-extrabold tracking-tight text-slate-900">Passwort zuruecksetzen</h2>
            </div>
          </div>
          <p className="mb-6 text-sm leading-relaxed" style={{ color: 'var(--nb-muted)' }}>Vergib ein neues Passwort fuer dein Konto.</p>
          <input
            type="password"
            placeholder="Neues Passwort"
            className="mb-3 w-full rounded-2xl border bg-white px-4 py-3 text-[16px] transition-colors focus:outline-none focus:ring-2"
            style={{ borderColor: 'var(--nb-border)' }}
            value={resetState.password}
            onChange={(e) => setResetState((s) => ({ ...s, password: e.target.value }))}
          />
          <input
            type="password"
            placeholder="Passwort wiederholen"
            className="mb-2 w-full rounded-2xl border bg-white px-4 py-3 text-[16px] transition-colors focus:outline-none focus:ring-2"
            style={{ borderColor: 'var(--nb-border)' }}
            value={resetState.confirmPassword}
            onChange={(e) => setResetState((s) => ({ ...s, confirmPassword: e.target.value }))}
          />
          <button
            className="mt-5 w-full rounded-full px-6 py-3.5 text-[16px] font-semibold text-white shadow-sm transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-50"
            style={{ backgroundColor: 'var(--nb-primary)' }}
            disabled={resetState.loading}
            onClick={handleResetPassword}
          >
            {resetState.loading ? 'Speichern…' : 'Passwort speichern'}
          </button>
          {resetState.error && <div className="mt-4 text-center text-sm font-medium text-[#FF3B30]">{resetState.error}</div>}
          {resetState.message && <div className="mt-4 text-center text-sm font-medium text-emerald-600">{resetState.message}</div>}
          <button
            type="button"
            className="mt-6 text-sm font-semibold hover:underline"
            style={{ color: 'var(--nb-primary)' }}
            onClick={() => { window.location.href = window.location.pathname; }}
          >
            Zurueck zum Login
          </button>
        </div>
      </div>
    );
  }

  if (!loginState.user && !localStorage.getItem('devsuite_user') && !companyId && !user) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 py-10 font-sans" style={{ background: 'radial-gradient(1200px 420px at 10% -10%, #dfe9ff 0, transparent 70%), var(--nb-bg)' }}>
        <div className="nb-card w-full max-w-[460px] p-8 md:p-10">
          <div className="mb-7 flex items-center gap-4">
            <img src="/Nextbill Icon.png" alt="Logo" className="h-16 w-16 rounded-2xl border object-contain p-2" style={{ borderColor: 'var(--nb-border)', backgroundColor: 'var(--nb-surface-soft)' }} />
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--nb-muted)' }}>NextBill</p>
              <h2 className="text-[26px] font-extrabold tracking-tight text-slate-900">Willkommen zurueck</h2>
            </div>
          </div>
          {renderLoginForm()}
        </div>
      </div>
    );
  }

  if (user && (user.is_admin === true || user.is_admin === 1 || user.is_admin === '1')) {
    return <AdminPanel />;
  }
  if (!companyId) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 py-10 font-sans" style={{ background: 'radial-gradient(1200px 420px at 10% -10%, #dfe9ff 0, transparent 70%), var(--nb-bg)' }}>
        <div className="nb-card w-full max-w-[460px] p-8 md:p-10">
          <div className="mb-7 flex items-center gap-4">
            <img src="/Nextbill Icon.png" alt="Logo" className="h-16 w-16 rounded-2xl border object-contain p-2" style={{ borderColor: 'var(--nb-border)', backgroundColor: 'var(--nb-surface-soft)' }} />
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--nb-muted)' }}>NextBill</p>
              <h2 className="text-[26px] font-extrabold tracking-tight text-slate-900">Willkommen zurueck</h2>
            </div>
          </div>
          {renderLoginForm()}
        </div>
      </div>
    );
  }

  // Main Render
  return (
    <>
      <div className="flex h-screen w-full font-sans text-slate-900 overflow-hidden print:hidden">
        {apiLoading && (
          <div className="fixed top-0 left-0 right-0 z-40 h-1 bg-slate-200 overflow-hidden">
            <div className="h-full w-1/3 bg-blue-500 animate-loading-bar" />
          </div>
        )}
        {apiError && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-red-50 border border-red-200 text-red-800 px-4 py-2 rounded-lg shadow-lg text-sm flex items-center gap-3">
            <span>{apiError}</span>
            <button type="button" onClick={() => setApiError(null)} className="text-red-600 hover:underline">
              Schließen
            </button>
          </div>
        )}
        {/* Desktop Sidebar */}
        <aside className="hidden md:flex flex-col w-64 text-slate-900 border-r h-screen sticky top-0" style={{ backgroundColor: 'var(--nb-surface-soft)', borderColor: 'var(--nb-border)' }}>
          <div className="p-8 pb-4 flex flex-col items-center text-center">
            {companySettings.logoUrl ? (
              <img src={companySettings.logoUrl} alt="" className="h-14 w-auto object-contain mb-3 rounded-xl" />
            ) : (
              <div className="w-14 h-14 bg-[#007AFF] text-white rounded-[16px] flex items-center justify-center text-xl font-bold shadow-sm mb-3">
                {companySettings.companyName?.substring(0, 2).toUpperCase() || 'NB'}
              </div>
            )}
            <span className="text-[20px] font-bold tracking-tight leading-tight text-slate-900">
              {companySettings.companyName || 'NextBill'}
            </span>
          </div>
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
            ].map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all font-medium touch-manipulation ${activeTab === item.id ? 'bg-[#007AFF] text-white shadow-sm' : 'text-slate-600 hover:bg-black/5'
                  }`}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </button>
            ))}

            <div className="pt-1">
              <button
                type="button"
                onClick={handleInvoicesMenuClick}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all font-medium touch-manipulation ${isInvoicesTabActive ? 'bg-[#007AFF] text-white shadow-sm' : 'text-slate-600 hover:bg-black/5'
                  }`}
              >
                <span className="flex items-center space-x-3">
                  <FileText size={20} />
                  <span>Rechnungen</span>
                </span>
                <ChevronDown size={18} className={`transition-transform ${isInvoicesMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {isInvoicesMenuOpen && (
                <div className="mt-1 ml-4 pl-3 border-l border-slate-300/80 space-y-1">
                  {[
                    { id: 'recurring', label: 'Wiederkehrend', active: activeTab === 'subscriptions' },
                    { id: 'reminders', label: 'Mahnungen', active: activeTab === 'invoices' && invoiceFilter === 'reminders' },
                    { id: 'credits', label: 'Gutschriften', active: activeTab === 'invoices' && invoiceFilter === 'credits' },
                  ].map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleInvoicesSubTabClick(item.id)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg transition-all font-medium touch-manipulation ${item.active ? 'bg-[#007AFF] text-white shadow-sm' : 'text-slate-600 hover:bg-black/5'
                        }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-1">
              <button
                type="button"
                onClick={handleOrdersMenuClick}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all font-medium touch-manipulation ${isOrdersTabActive ? 'bg-[#007AFF] text-white shadow-sm' : 'text-slate-600 hover:bg-black/5'
                  }`}
              >
                <span className="flex items-center space-x-3">
                  <FileInput size={20} />
                  <span>Aufträge</span>
                </span>
                <ChevronDown size={18} className={`transition-transform ${isOrdersMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {isOrdersMenuOpen && (
                <div className="mt-1 ml-4 pl-3 border-l border-slate-300/80 space-y-1">
                  {[
                    { id: 'quotes', label: 'Angebote' },
                    { id: 'order-confirmations', label: 'Auftragsbestätigungen' },
                    { id: 'delivery-notes', label: 'Lieferscheine' },
                  ].map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleOrdersSubTabClick(item.id)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg transition-all font-medium touch-manipulation ${activeTab === item.id ? 'bg-[#007AFF] text-white shadow-sm' : 'text-slate-600 hover:bg-black/5'
                        }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {[
              { id: 'customers', label: 'Kunden', icon: Users },
              { id: 'tasks', label: 'Aufgaben', icon: Clock },
              { id: 'reports', label: 'Berichte', icon: BarChart3 },
              { id: 'sent-emails', label: 'Gesendete E-Mails', icon: Mail },
            ].map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all font-medium touch-manipulation ${activeTab === item.id ? 'bg-[#007AFF] text-white shadow-sm' : 'text-slate-600 hover:bg-black/5'
                  }`}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
          <div className="p-4 border-t border-[#E5E5EA]">
            <button
              type="button"
              onClick={() => setShowSettings(true)}
              className="flex items-center space-x-3 text-slate-600 hover:text-slate-900 hover:bg-black/5 rounded-xl transition-all w-full px-4 py-2.5 font-medium touch-manipulation"
            >
              <SettingsIcon size={20} />
              <span>Einstellungen</span>
            </button>
            <button
              type="button"
              onClick={() => {
                localStorage.removeItem(COMPANY_ID_KEY);
                localStorage.removeItem('devsuite_user');
                window.location.reload();
              }}
              className="flex items-center space-x-3 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-xl transition-all w-full px-4 py-2.5 mt-2 font-medium touch-manipulation"
            >
              <LogOut size={20} />
              <span>Abmelden</span>
            </button>
          </div>
        </aside>

        {/* Mobile Header (iOS Style) */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 pb-[84px] md:pb-0"> {/* padding-bottom to account for tab bar on mobile */}
          <header className="backdrop-blur-md border-b border-slate-200/50 pt-safe-t h-14 flex items-center justify-between px-4 sticky top-0 z-30 md:hidden" style={{ backgroundColor: 'rgba(248,250,254,0.85)' }}>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-800 text-[17px]">{companySettings.companyName || 'NextBill'}</span>
            </div>
            <button type="button" onClick={() => setShowSettings(true)} className="p-2 text-[#007AFF]">
              <SettingsIcon size={24} />
            </button>
          </header>

          {/* Main Content */}
          <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto min-h-0 w-full max-w-7xl mx-auto">
            <div className="mx-auto w-full">
              <div className="mb-4 relative" ref={globalSearchRef}>
                <input
                  type="text"
                  value={globalSearchQuery}
                  onChange={(e) => { setGlobalSearchQuery(e.target.value); setGlobalSearchOpen(true); }}
                  onFocus={() => setGlobalSearchOpen(true)}
                  placeholder="Volltextsuche: Kunden, Rechnungen, Angebote, Notizen, Dokumente..."
                  className="w-full p-3 min-h-[44px] border border-slate-300 rounded-xl bg-white"
                />
                {globalSearchOpen && (globalSearchQuery || '').trim().length >= 2 && (
                  <div className="absolute z-30 mt-2 w-full bg-white border border-slate-200 rounded-xl shadow-lg p-3 max-h-[420px] overflow-auto space-y-2">
                    {[
                      { key: 'customers', label: 'Kunden', format: (c) => `${c.name}${c.email ? ` · ${c.email}` : ''}`, onClick: (c) => { setActiveTab('customers'); setViewCustomerId(c.id); setGlobalSearchOpen(false); } },
                      { key: 'invoices', label: 'Rechnungen', format: (i) => `${i.id} · ${getCustomerNameById(i.customerId)} · ${formatDate(i.date)}`, onClick: () => { setActiveTab('invoices'); setGlobalSearchOpen(false); } },
                      { key: 'quotes', label: 'Angebote', format: (q) => `${q.id} · ${getCustomerNameById(q.customer_id || q.customerId)} · ${formatDate(q.date)}`, onClick: () => { setActiveTab('quotes'); setGlobalSearchOpen(false); } },
                      { key: 'subscriptions', label: 'Abos', format: (s) => `${s.id || ''} · ${s.description || ''} · ${getCustomerNameById(s.customer_id || s.customerId)}`, onClick: () => { setActiveTab('subscriptions'); setGlobalSearchOpen(false); } },
                      { key: 'tasks', label: 'Aufgaben', format: (t) => `${t.title || ''}${t.description ? ` · ${t.description}` : ''}`, onClick: () => { setActiveTab('tasks'); setGlobalSearchOpen(false); } },
                      { key: 'notes', label: 'Notizen', format: (n) => `${getCustomerNameById(n.customer_id || n.customerId)} · ${(n.content || '').slice(0, 80)}`, onClick: (n) => { setActiveTab('customers'); setViewCustomerId(n.customer_id || n.customerId); setGlobalSearchOpen(false); } },
                      { key: 'documents', label: 'Dokumente', format: (d) => `${getCustomerNameById(d.customer_id || d.customerId)} · ${d.title || d.original_name || d.originalName}`, onClick: (d) => { setActiveTab('customers'); setViewCustomerId(d.customer_id || d.customerId); setGlobalSearchOpen(false); } },
                    ].map((section) => {
                      const items = Array.isArray(globalSearchResults?.[section.key]) ? globalSearchResults[section.key] : [];
                      if (items.length === 0) return null;
                      return (
                        <div key={section.key}>
                          <p className="text-xs uppercase text-slate-400 font-semibold px-1 mb-1">{section.label}</p>
                          <div className="space-y-1">
                            {items.slice(0, 5).map((item, idx) => (
                              <button
                                key={`${section.key}-${idx}`}
                                type="button"
                                onClick={() => section.onClick(item)}
                                className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100 text-sm text-slate-700"
                              >
                                {section.format(item)}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    {Object.values(globalSearchResults || {}).every((v) => !Array.isArray(v) || v.length === 0) && (
                      <div className="text-sm text-slate-500 px-2 py-1">Keine Treffer</div>
                    )}
                  </div>
                )}
              </div>


              <Suspense fallback={<div className="p-10 text-center text-slate-500">Laden...</div>}>
                {activeTab === 'dashboard' && (
                  <Dashboard
                    stats={stats}
                    invoices={invoices}
                    quotes={quotes}
                    subscriptions={subscriptions}
                    customers={customers}
                    companySettings={companySettings}
                    tasks={tasks}
                    setActiveTab={setActiveTab}
                    openNewInvoiceForm={openNewInvoiceForm}
                    onCreateQuote={openNewQuoteForm}
                    onCreateCustomer={openNewCustomerForm}
                    onCreateSubscription={openNewSubscriptionForm}
                    handleDashboardCardClick={handleDashboardCardClick}
                  />
                )}
                {activeTab === 'invoices' && (
                  <Invoices
                    invoices={invoices}
                    customers={customers}
                    companySettings={companySettings}
                    isEditingInvoice={isEditingInvoice}
                    setIsEditingInvoice={setIsEditingInvoice}
                    currentInvoice={currentInvoice}
                    setCurrentInvoice={setCurrentInvoice}
                    invoiceFilter={invoiceFilter}
                    setInvoiceFilter={setInvoiceFilter}
                    csvDateFrom={csvDateFrom}
                    setCsvDateFrom={setCsvDateFrom}
                    csvDateTo={csvDateTo}
                    setCsvDateTo={setCsvDateTo}
                    handleSaveInvoice={handleSaveInvoice}
                    handleDuplicateInvoice={handleDuplicateInvoice}
                    createCreditOrStorno={createCreditOrStorno}
                    createInstallmentPlan={createInstallmentPlan}
                    handleSendInvoiceEmail={handleSendInvoiceEmail}
                    updateInvoiceStatus={updateInvoiceStatus}
                    openMarkPaidModal={openMarkPaidModal}
                    exportToCSV={exportToCSV}
                    exportDatevCsv={exportDatevCsv}
                    showToast={showToast}
                    showConfirm={showConfirm}
                    openNewInvoiceForm={openNewInvoiceForm}
                    setPdfGenData={setPdfGenData}
                    onDownloadPdf={handleDownloadPdf}
                    setPreviewDoc={openPdfPreview}
                    setInvoices={setInvoices}
                    onDeleteDraftInvoice={handleDeleteInvoiceDraft}
                    nextInvoiceId={nextInvoiceId}
                    setNextInvoiceId={setNextInvoiceId}
                  />

                )}
                {activeTab === 'quotes' && (
                  <Quotes
                    quotes={quotes}
                    customers={customers}
                    companySettings={companySettings}
                    isEditingQuote={isEditingQuote}
                    setIsEditingQuote={setIsEditingQuote}
                    currentQuote={currentQuote}
                    setCurrentQuote={setCurrentQuote}
                    handleSaveQuote={handleSaveQuote}
                    handleDuplicateQuote={handleDuplicateQuote}
                    handleSendQuoteEmail={handleSendQuoteEmail}
                    handleQuoteToInvoice={handleQuoteToInvoice}
                    handleDeleteQuote={handleDeleteQuote}
                    showToast={showToast}
                    showConfirm={showConfirm}
                    openNewQuoteForm={openNewQuoteForm}
                    setPdfGenData={setPdfGenData}
                    onDownloadPdf={handleDownloadPdf}
                    setPreviewDoc={openPdfPreview}
                  />
                )}
                {(activeTab === 'order-confirmations' || activeTab === 'delivery-notes') && (
                  <div className="nb-card p-8">
                    <h1 className="text-[22px] sm:text-[28px] font-bold text-slate-900 tracking-tight">
                      {activeTab === 'order-confirmations' ? 'Auftragsbestätigungen' : 'Lieferscheine'}
                    </h1>
                    <p className="text-[14px] text-slate-600 mt-2">
                      Dieser Bereich wird im selben NextBill-Design vorbereitet. Bis dahin findest du deine Angebotsdaten unter
                      {' '}
                      <button
                        type="button"
                        onClick={() => setActiveTab('quotes')}
                        className="text-[#007AFF] font-semibold hover:underline"
                      >
                        Aufträge &gt; Angebote
                      </button>
                      .
                    </p>
                  </div>
                )}
                {activeTab === 'subscriptions' && (
                  <Subscriptions
                    subscriptions={subscriptions}
                    customers={customers}
                    companySettings={companySettings}
                    isEditingSubscription={isEditingSubscription}
                    setIsEditingSubscription={setIsEditingSubscription}
                    currentSubscription={currentSubscription}
                    setCurrentSubscription={setCurrentSubscription}
                    handleSaveSubscription={handleSaveSubscription}
                    handleDeleteSubscription={handleDeleteSubscription}
                    runSubscription={runSubscription}
                    showToast={showToast}
                  />
                )}
                {activeTab === 'customers' && (
                  <Customers
                    customers={customers}
                    setCustomers={setCustomers}
                    invoices={invoices}
                    quotes={quotes}
                    subscriptions={subscriptions}
                    companySettings={companySettings}
                    tasks={tasks}
                    sentEmails={sentEmails}
                    setTasks={setTasks}
                    isEditingCustomer={isEditingCustomer}
                    setIsEditingCustomer={setIsEditingCustomer}
                    currentCustomer={currentCustomer}
                    setCurrentCustomer={setCurrentCustomer}
                    viewCustomerId={viewCustomerId}
                    setViewCustomerId={setViewCustomerId}
                    handleSaveCustomer={handleSaveCustomer}
                    handleDeleteCustomer={handleDeleteCustomer}
                    showToast={showToast}
                  />
                )}
                {activeTab === 'tasks' && (
                  <Tasks
                    tasks={tasks}
                    setTasks={setTasks}
                    customers={customers}
                    isEditingTask={isEditingTask}
                    setIsEditingTask={setIsEditingTask}
                    currentTask={currentTask}
                    setCurrentTask={setCurrentTask}
                    handleSaveTask={handleSaveTask}
                    showToast={showToast}
                  />
                )}
                {activeTab === 'reports' && (
                  <Reports
                    invoices={invoices}
                    quotes={quotes}
                    customers={customers}
                    stats={stats}
                    companySettings={companySettings}
                  />
                )}
                {activeTab === 'sent-emails' && (
                  <SentEmails
                    sentEmails={sentEmails}
                    setSentEmails={setSentEmails}
                    onOpenDocument={handleOpenSentEmailDocument}
                  />
                )}
              </Suspense>
            </div>
          </main>

          {/* Mobile Quick Actions */}
          <div className="md:hidden fixed bottom-16 right-3 z-40 flex flex-col gap-2">
            <button
              type="button"
              onClick={openNewInvoiceForm}
              className="px-3 py-2 rounded-full bg-[#007AFF] text-white text-xs font-semibold shadow"
            >
              + Rechnung
            </button>
            <button
              type="button"
              onClick={openNewQuoteForm}
              className="px-3 py-2 rounded-full bg-[#FF9500] text-white text-xs font-semibold shadow"
            >
              + Angebot
            </button>
            <button
              type="button"
              onClick={openNewCustomerForm}
              className="px-3 py-2 rounded-full bg-white border border-slate-300 text-slate-700 text-xs font-semibold shadow"
            >
              + Kunde
            </button>
            <button
              type="button"
              onClick={() => { setInvoiceFilter('open'); setActiveTab('invoices'); }}
              className="px-3 py-2 rounded-full bg-white border border-slate-300 text-slate-700 text-xs font-semibold shadow"
            >
              Offene Zahlungen
            </button>
          </div>

          {/* iOS Bottom Tab Bar (Mobile only) */}
          <div className="md:hidden fixed bottom-0 left-0 right-0 bg-[#F2F2F7]/80 backdrop-blur-md border-t border-slate-200/50 pb-safe-b z-40">
            <nav className="flex items-center justify-around h-14 px-2">
              {[
                { id: 'dashboard', label: 'Home', icon: LayoutDashboard },
                { id: 'invoices', label: 'Rechnungen', icon: FileText },
                { id: 'quotes', label: 'Angebote', icon: FileInput },
                { id: 'customers', label: 'Kunden', icon: Users },
                { id: 'tasks', label: 'Aufgaben', icon: Clock },
              ].map((item) => {
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`flex flex-col items-center justify-center w-full h-full space-y-1 ${isActive ? 'text-[#007AFF]' : 'text-slate-400'}`}
                  >
                    <item.icon size={24} strokeWidth={isActive ? 2.5 : 2} />
                    <span className="text-[10px] font-medium">{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {showSettings && (
        <SettingsModal
          settings={companySettings}
          user={user}
          onSave={handleSaveSettings}
          onUploadLogo={async (file) => {
            if (!hasApi()) {
              throw new Error('Logo-Upload ist nur mit aktivem API-Server verfügbar.');
            }
            return api.apiUploadLogo(file);
          }}
          onClose={() => setShowSettings(false)}
          exportBackup={exportBackup}
          importBackup={importBackup}
        />
      )}

      {/* Helper Components */}
      <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />
      <ConfirmModal
        isOpen={!!confirmDialog}
        message={confirmDialog?.message}
        onConfirm={() => { confirmDialog?.onConfirm(); setConfirmDialog(null); }}
        onCancel={() => { confirmDialog?.onCancel(); setConfirmDialog(null); }}
        onClose={() => { setConfirmDialog(null); }}
        confirmLabel={confirmDialog?.confirmLabel}
        cancelLabel={confirmDialog?.cancelLabel}
      />
      {paidModalInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold mb-4">Zahlungseingang bestätigen</h3>
            <p className="text-sm text-slate-600 mb-4">
              Rechnung {paidModalInvoice.id}<br />
              Wann wurde die Zahlung empfangen?
            </p>
            <input
              type="date"
              className="w-full p-2 border border-slate-300 rounded mb-4"
              value={paidDateInput}
              onChange={(e) => setPaidDateInput(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPaidModalInvoice(null)}
                className="px-4 py-2 rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => updateInvoiceStatus(paidModalInvoice.id, 'paid', paidDateInput)}
                className="px-4 py-2 rounded bg-green-600 hover:bg-green-700 text-white"
              >
                Als bezahlt markieren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Container (Invisible) */}
      <div className="fixed top-0 left-0 pointer-events-none opacity-0" style={{ zIndex: -100 }}>
        <div ref={pdfContainerRef} style={{ width: '210mm', minHeight: '297mm', background: 'white' }}>
          {pdfGenData && (
            pdfGenData.type === 'quote' ? (
              <QuotePdfContent
                documentData={pdfGenData.doc}
                customer={pdfGenData.customer}
                settings={pdfGenData.settings}
              />
            ) : (
              <InvoicePdfContent
                documentData={pdfGenData.doc}
                customer={pdfGenData.customer}
                settings={pdfGenData.settings}
                type={pdfGenData.type}
              />
            )
          )}
        </div>
      </div>

      {previewDoc && (
        <PrintView
          documentData={previewDoc.doc}
          customer={previewDoc.customer}
          type={previewDoc.type}
          pdfUrl={previewDoc.pdfUrl}
          htmlUrl={previewDoc.htmlUrl}
          settings={companySettings}
          onDownloadPdf={handleDownloadPdf}
          onClose={() => setPreviewDoc(null)}
        />
      )}

      {emailSending && (
        <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-xl border border-slate-200 p-4 z-50 flex items-center gap-3 w-80 animate-in slide-in-from-bottom-5">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-900">Sende E-Mail...</p>
            <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2 overflow-hidden">
              <div
                className="bg-blue-600 h-1.5 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${emailProgress}%` }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
const Search = ({ size, className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size || 24} height={size || 24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
);
